import { computed, createApp, h, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { saveAs } from 'file-saver'
import pptxgen from 'pptxgenjs'
import tinycolor from 'tinycolor2'
import { captureDomToBlob, captureDomToJpeg } from '@/utils/domScreenshot'
import { jsPDF } from 'jspdf'
import JSZip from 'jszip'
import { PDFDocument } from 'pdf-lib'
import { ElMessage, ElButton, dayjs } from 'element-plus'
import { useSlidesStore } from '@/store'
import { isImageLikeElement, type PPTElementOutline, type PPTElementShadow, type PPTElementLink, type PPTImageLikeElement, type PPTShapeElement, type ImageElementFilters, type Slide } from '@/types/slides'
import { CLIPPATHS, ClipPathTypes } from '@/configs/imageClip'
import { getElementRange, getLineElementPath, getTableSubThemeColor } from '@/utils/element'
import { type AST, toAST } from '@/utils/htmlParser'
import { type SvgPoints, toPoints } from '@/utils/svgPathParser'
import { encrypt } from '@/utils/crypto'
import { svg2Base64 } from '@/utils/svg2Base64'
import { isMobile, getQueryParam, reportEvent, isMiniAppWebview, requestMiniAppDownload, requestMiniAppDownloadSafe } from '@/utils/auth'
import message from '@/utils/message'
import { injectPptxAnimations } from '@/utils/pptxAnimationInject'
import { exportPptx, exportPptxStream } from '@/api/export'
import ConfirmModal from '@/components/YiDian/ConfirmModal/index.vue'

interface ExportImageConfig {
  quality: number
  width: number
  fontEmbedCSS?: string
}

export interface ExportPDFPageSize {
  width: number
  height: number
  margin: number
}

interface ExportProgressOptions {
  disableLoading?: boolean
  onProgress?: (progress: number) => void
}

interface ExportPDFOptions extends ExportProgressOptions {
  slidesPerPage?: number
  /** 仅生成 PDF Blob，不触发下载/上传（供移动端分批渲染后合并） */
  outputBlobOnly?: boolean
  /** 分批导出中间页：跳过 AIGC 元数据注入（合并后再统一注入） */
  skipAigcMetadata?: boolean
  /** 分批导出中间页：不切换 exporting 状态（由 beginMobileExportBatch / endMobileExportBatch 管理） */
  batchPdfPart?: boolean
  /** PDF 导出是否在当次截图上叠加水印；默认仅整份 PDF 的第一页（pageIndex===0）加水印 */
  applyPdfWatermark?: boolean
}

/** 纯前端判断是否为 iPhone 12 及以下机型（含 12），用于导出时降低 CHUNK_SIZE 减轻内存压力 */
function isIPhone12OrBelow(): boolean {
  if (!/iPhone|iPad|iPod/i.test(navigator.userAgent)) return false
  // 灵动岛 (iPhone 14 Pro / 15 Pro / 16) safe-area > 50，可确定是 13 代以上
  const el = document.createElement('div')
  el.style.cssText = 'position:absolute;top:0;left:0;visibility:hidden;height:env(safe-area-inset-top,0px)'
  document.body.appendChild(el)
  const safeTop = parseFloat(getComputedStyle(el).height) || 0
  document.body.removeChild(el)
  if (safeTop > 50) return false // 灵动岛 = 14 Pro+，非 12 及以下
  // 小屏无刘海 (iPhone 8/SE)：safe-area 约 20，屏高 <= 736
  const w = window.screen.width
  const h = window.screen.height
  if (h <= 736 || (w <= 414 && h <= 667)) return true // 8/SE 代
  // 刘海屏但非灵动岛：X~14 非 Pro，无法区分 12/13，保守按 12 及以下处理
  return true
}

/** 安卓：canvas 单边像素上限 */
const MOBILE_EXPORT_MAX_CANVAS_EDGE = 6144
/**
 * iOS：html-to-image 走 SVG foreignObject → canvas，单边像素上限（与 pixelRatio 一起钳制）。
 * - 判断为「非 iPhone 12 及以下」的机型（含多数 13+、带灵动岛机型）用 MODERN。
 * - 判断为 12 及以下：仍设 LEGACY 上限；若在 12 上把上限改到 2048 仍全黑，通常不是「边长不够小」而是下面几类，单靠压 canvas 往往无解：
 *   1) WKWebView 对 foreignObject + 复杂 DOM/GPU 解码失败，drawImage 得到空纹理 → 整图黑；
 *   2) 幻灯片内外链图片无 CORS，canvas 被污染（安全限制）→ 黑或透明叠成黑；
 *   3) 内存峰值仍爆（与单页逻辑尺寸、素材有关），与 2048/4096 数值无单调关系。
 */
const MOBILE_EXPORT_MAX_CANVAS_EDGE_IOS_MODERN = 5120
const MOBILE_EXPORT_MAX_CANVAS_EDGE_IOS_LEGACY = 4096
/** scrollHeight 偶发异常偏大时限制逻辑高度，避免 toBlob 画布仍过大全黑 */
const MOBILE_CAPTURE_MAX_LOGICAL_H = 8192

type CaptureStep = { width: number; quality: number; pixelRatio: number }
/** 移动端截图：清晰度与内存折中；实际 pixelRatio 还会按 MOBILE_EXPORT_MAX_CANVAS_EDGE 再钳制 */
const MOBILE_CAPTURE_STEPS: CaptureStep[] = [
  { width: 720, quality: 0.95, pixelRatio: 1.78 },
  { width: 640, quality: 0.9, pixelRatio: 1.52 },
  { width: 560, quality: 0.84, pixelRatio: 1.28 },
  { width: 480, quality: 0.77, pixelRatio: 1.1 },
  { width: 420, quality: 0.7, pixelRatio: 1 },
]

function getMobileCapturePixelRatio(step: CaptureStep, isIosExportImage: boolean): number {
  if (isIosExportImage && isIosExportLikeAndroidTestEnabled()) {
    return Math.min(1.65, step.pixelRatio)
  }
  if (isIPhone12OrBelow() && isIosExportImage) return 1
  if (!isIosExportImage) return Math.min(1.65, step.pixelRatio)
  return Math.min(1.48, step.pixelRatio)
}

/** 读取节点在布局中的内容宽度（px），避免 toBlob 的 width 小于实际 DOM 时右侧被裁切（多页截容器时常见） */
function readNodeContentWidthPx(node: HTMLElement): number {
  let w = Math.max(node.scrollWidth, node.offsetWidth, node.clientWidth)
  try {
    const r = node.getBoundingClientRect()
    if (Number.isFinite(r.width) && r.width > 1) w = Math.max(w, r.width)
  } catch {
    /* ignore */
  }
  return Math.max(1, Math.ceil(w))
}

/** 与 ?exportDebug=1 或 sessionStorage.setItem('ydppt_export_debug','1') 配合，在控制台输出移动端导出诊断 */
const EXPORT_DEBUG_SESSION_KEY = 'ydppt_export_debug'

/**
 * 实验开关：在 **iOS WebView 导出**（`prompt` 且无 `JsAndroid`）时，临时按 **安卓** 的「每批 4 页 + 内层多页一截 + 与安卓一致的 pixelRatio / canvas edge」策略，便于对比。
 * 开启：`?iosExportLikeAndroid=1` 或 `sessionStorage.setItem('ydppt_ios_export_like_android','1')`
 * 说明：与 `isIPhone12OrBelow()` 解耦，任意满足 iOS 导出条件的机型都可开开关验证；默认关闭不影响现网。
 */
const IOS_EXPORT_LIKE_ANDROID_SESSION_KEY = 'ydppt_ios_export_like_android'

function isIosExportLikeAndroidTestEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (getQueryParam('iosExportLikeAndroid') === '1') return true
    if (window.sessionStorage?.getItem(IOS_EXPORT_LIKE_ANDROID_SESSION_KEY) === '1') return true
  } catch {
    /* ignore */
  }
  return false
}

function isMobileExportDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    if (getQueryParam('exportDebug') === '1') return true
    if (window.sessionStorage?.getItem(EXPORT_DEBUG_SESSION_KEY) === '1') return true
  } catch {
    /* ignore */
  }
  return false
}

function logMobileExportDebug(tag: string, payload: Record<string, unknown>) {
  if (!isMobileExportDebugEnabled()) return
  console.warn(`[exportDebug:${tag}]`, { ...payload, _ts: new Date().toISOString() })
}

export interface NativeSaveProgressHandler {
  onProgress?: (progress: number) => void
  onComplete?: (status: 'success' | 'fail', path?: string) => void
}

let nativeSaveProgressHandler: NativeSaveProgressHandler | null = null

/** 把原生回调里各种形态的进度值统一成 0–100；无法识别则返回 null */
function coerceNativeProgressPercent(...candidates: unknown[]): number | null {
  for (const raw of candidates) {
    if (raw == null || raw === '') continue
    if (typeof raw === 'string') {
      const trimmed = raw.trim()
      if (/^(success|fail|error|complete|ok|done|cancel|canceled|cancelled|progress|downloading|download)$/i.test(trimmed)) continue
      // 纯文件路径，不是进度
      if (/^[a-z]:\\/i.test(trimmed) || trimmed.startsWith('/') || trimmed.includes('://')) continue
      // "45%" / "progress:45" / "percent 45"
      const labeled = trimmed.match(/^(?:progress|percent|rate|p)[:\s]+(\d+(?:\.\d+)?)\s*%?$/i)
      if (labeled) {
        const n = Number.parseFloat(labeled[1])
        if (!Number.isNaN(n) && n >= 0 && n <= 100) return Math.round(n)
      }
    }
    const text = String(raw).replace(/%/g, '').trim()
    const n = typeof raw === 'number' ? raw : Number.parseFloat(text)
    if (Number.isNaN(n) || !Number.isFinite(n)) continue
    // 0~1 小数当作比例（排除恰好 0/1 的整型百分比歧义：带小数点才按比例）
    if (n > 0 && n <= 1 && (typeof raw === 'number' ? !Number.isInteger(raw) : /\./.test(text))) {
      return Math.min(100, Math.max(0, Math.round(n * 100)))
    }
    if (n >= 0 && n <= 100) return Math.round(n)
  }
  return null
}

function normalizeNativeDownProgressArgs(
  statusOrPayload: unknown,
  path?: unknown,
  progress?: unknown,
): { status: string; path: string; progress?: number | string } {
  // 单参：JSON 字符串 / 纯数字进度 / { status, path, progress }
  if (path === undefined && progress === undefined) {
    if (typeof statusOrPayload === 'number') {
      return { status: 'progress', path: '', progress: statusOrPayload }
    }
    if (typeof statusOrPayload === 'string') {
      const trimmed = statusOrPayload.trim()
      if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
        try {
          const obj = JSON.parse(trimmed) as Record<string, unknown>
          return normalizeNativeDownProgressArgs(obj)
        } catch {
          /* fallthrough */
        }
      }
      const asNum = Number.parseFloat(trimmed.replace(/%/g, ''))
      if (!Number.isNaN(asNum) && trimmed.replace(/%/g, '').trim() !== '' && !/[a-z]/i.test(trimmed.replace('%', ''))) {
        return { status: 'progress', path: '', progress: asNum }
      }
      return { status: trimmed, path: '', progress: undefined }
    }
    if (statusOrPayload && typeof statusOrPayload === 'object') {
      const obj = statusOrPayload as Record<string, unknown>
      return {
        status: String(obj.status ?? obj.state ?? obj.type ?? 'progress'),
        path: String(obj.path ?? obj.filePath ?? obj.file ?? ''),
        progress: (obj.progress ?? obj.percent ?? obj.value ?? obj.rate) as number | string | undefined,
      }
    }
  }
  // 两参且第二参是数字：当作 (status, progress)
  if (progress === undefined && (typeof path === 'number' || (typeof path === 'string' && /^\d+(\.\d+)?%?$/.test(path.trim())))) {
    return {
      status: String(statusOrPayload ?? 'progress'),
      path: '',
      progress: path as number | string,
    }
  }
  return {
    status: String(statusOrPayload ?? ''),
    path: String(path ?? ''),
    progress: progress as number | string | undefined,
  }
}

function parseNativeSaveProgressValue(status: string, path: string, progress?: number | string): number | null {
  const statusNorm = String(status ?? '').trim().toLowerCase()
  if (statusNorm === 'success' || statusNorm === 'fail' || statusNorm === 'error') return null
  return coerceNativeProgressPercent(progress, path, status)
}

function handleNativeDownProgress(
  statusOrPayload: unknown,
  path?: unknown,
  progress?: unknown,
) {
  const normalized = normalizeNativeDownProgressArgs(statusOrPayload, path, progress)
  const { status, path: filePath, progress: progressRaw } = normalized
  console.log('[native downProgress]', {
    raw: { statusOrPayload, path, progress },
    normalized,
    hasHandler: !!nativeSaveProgressHandler,
  })
  const progressVal = parseNativeSaveProgressValue(status, filePath, progressRaw)
  if (progressVal !== null) {
    nativeSaveProgressHandler?.onProgress?.(progressVal)
    return
  }
  const statusNorm = String(status ?? '').trim().toLowerCase()
  if (statusNorm === 'success' || statusNorm === 'ok' || statusNorm === 'complete' || statusNorm === 'done') {
    nativeSaveProgressHandler?.onComplete?.('success', filePath)
    if (window?.JsAndroid && window.JsAndroid?.downloadCompletedTip) {
      (window.JsAndroid as any).downloadCompletedTip('文件下载成功，请在文件管理器中查看')
    }
    ElMessage.success({
      message: `<div style="max-width: 300px; word-wrap: break-word;">文档已保存到${filePath}</div>`,
      offset: 60,
      grouping: true,
      dangerouslyUseHTMLString: true,
    })
  } else if (statusNorm === 'fail' || statusNorm === 'error') {
    nativeSaveProgressHandler?.onComplete?.('fail')
    ElMessage.error({ message: '保存失败', offset: 60 })
  }
}

function snapshotDomForExportDebug(root: HTMLElement, maxImgs = 8): Record<string, unknown> {
  const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[]
  return {
    imgCount: imgs.length,
    svgImageCount: root.querySelectorAll('image').length,
    imgSamples: imgs.slice(0, maxImgs).map((img) => ({
      naturalW: img.naturalWidth,
      naturalH: img.naturalHeight,
      complete: img.complete,
      crossOrigin: img.crossOrigin,
      src: (img.currentSrc || img.src || '').slice(0, 240),
    })),
  }
}

/** Android 导出图片：每张照片合并的幻灯片数（3～4 页一张图；iOS 逻辑单独分支，不用此值） */
const ANDROID_EXPORT_IMAGE_CHUNK_SIZE = 4

/** iOS downFile：在扩展名前追加 `_YYYYMMDDHHmmss`（14 位，精确到秒） */

const getMobilePdfCaptureSteps = (): CaptureStep[] => {
  if (isIPhone12OrBelow()) {
    return [
      { width: 1600, quality: 0.95, pixelRatio: 1.5 },
      { width: 1600, quality: 0.93, pixelRatio: 1 },
      { width: 1200, quality: 0.92, pixelRatio: 1 },
    ]
  }
  return [
    { width: 1600, quality: 0.95, pixelRatio: 2 },
    { width: 1600, quality: 0.95, pixelRatio: 1.5 },
    { width: 1600, quality: 0.93, pixelRatio: 1 },
  ]
}

/** 移动端 PDF 截图 pixelRatio 钳制（与图片导出一致，防止 canvas 单边过大 OOM） */
function getMobilePdfCapturePixelRatio(
  step: CaptureStep,
  width: number,
  height: number,
  isIosPdfExport: boolean,
): number {
  const edgeCap = isIosPdfExport
    ? (isIPhone12OrBelow() ? MOBILE_EXPORT_MAX_CANVAS_EDGE_IOS_LEGACY : MOBILE_EXPORT_MAX_CANVAS_EDGE_IOS_MODERN)
    : MOBILE_EXPORT_MAX_CANVAS_EDGE
  let pixelRatio = isIPhone12OrBelow() && isIosPdfExport
    ? Math.min(step.pixelRatio, 1)
    : step.pixelRatio
  pixelRatio = Math.min(
    pixelRatio,
    edgeCap / Math.max(1, width),
    edgeCap / Math.max(1, height),
  )
  return Math.max(1, pixelRatio)
}

/** 分批导出页间让出主线程，便于 WebView 回收截图/画布内存 */
async function yieldMobileExportGc(ms = 200) {
  await new Promise((resolve) => requestAnimationFrame(resolve))
  await new Promise((resolve) => setTimeout(resolve, ms))
}

function appendTimestampForIosFileName(fileName: string): string {
  const ts = dayjs().format('YYYYMMDDHHmmss')
  const dot = fileName.lastIndexOf('.')
  if (dot <= 0 || dot === fileName.length - 1) return `${fileName}_${ts}`
  return `${fileName.slice(0, dot)}_${ts}${fileName.slice(dot)}`
}

/** 导出主文件名（无扩展名）：仅保留文字、数字和下划线，避免 Markdown/符号导致导出异常 */
function sanitizeExportBaseName(name: string): string {
  let s = String(name ?? '').normalize('NFKC')
  s = s.replace(/\s+/gu, '_')
  s = s.replace(/[^\p{L}\p{M}\p{N}_]/gu, '')
  s = s.replace(/_+/g, '_').replace(/^_+|_+$/g, '')
  if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])$/i.test(s)) s = `_${s}`
  if (s.length > 120) s = Array.from(s).slice(0, 120).join('')
  return s || '未命名'
}

/** 含扩展名的完整导出文件名 */
function sanitizeExportFileName(fileName: string): string {
  const raw = String(fileName ?? '').trim()
  const dot = raw.lastIndexOf('.')
  if (dot <= 0) return sanitizeExportBaseName(raw)
  const ext = raw.slice(dot + 1).replace(/[^\w.+-]/gi, '').slice(0, 16)
  const base = sanitizeExportBaseName(raw.slice(0, dot))
  return ext ? `${base}.${ext}` : base
}

/** 标记 uploadFileToNative 链路错误，便于与生成 pptx/图片/PDF 失败区分提示 */
const EXPORT_UPLOAD_ERROR_MARK = '[export-upload]'

/** 后端已返回下载链接，但浏览器自动下载失败 */
const EXPORT_URL_DOWNLOAD_MARK = '[export-url-download]'

type ExportUrlDownloadError = Error & { downloadUrl: string }

function isExportUploadError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err ?? '')
  return (
    msg.includes(EXPORT_UPLOAD_ERROR_MARK)
    || /上传|文件签名|getFileSign/i.test(msg)
  )
}

function createExportUrlDownloadError(downloadUrl: string, cause?: unknown): ExportUrlDownloadError {
  const detail = cause instanceof Error ? cause.message : String(cause ?? '下载失败')
  const err = new Error(`${EXPORT_URL_DOWNLOAD_MARK}${detail}`) as ExportUrlDownloadError
  err.name = 'ExportUrlDownloadError'
  err.downloadUrl = downloadUrl
  return err
}

function getExportDownloadUrl(err: unknown): string | null {
  if (err && typeof err === 'object' && 'downloadUrl' in err) {
    const url = (err as { downloadUrl?: unknown }).downloadUrl
    if (typeof url === 'string' && url) return url
  }
  return null
}

/** 已有下载链接时，引导用户手动打开链接下载（样式对齐 ConfirmModal） */
function showPptManualDownloadDialog(downloadUrl: string) {
  const container = document.createElement('div')
  document.body.appendChild(container)

  const app = createApp({
    setup() {
      const visible = ref(true)
      let destroyed = false

      const destroy = () => {
        if (destroyed) return
        destroyed = true
        window.setTimeout(() => {
          app.unmount()
          container.remove()
        }, 280)
      }

      const close = () => {
        visible.value = false
        destroy()
      }

      return () => h(ConfirmModal, {
        modelValue: visible.value,
        'onUpdate:modelValue': (val: boolean) => {
          visible.value = val
          if (!val) destroy()
        },
        title: '提示',
        content: 'PPT下载失败，请点击下方的下载按钮进行下载',
        confirmText: '下载',
        cancelText: '关闭',
        webWidth: '420px',
        width: '85%',
        customClass: 'ConfirmModal rounded-[20px]',
        onConfirm: () => {
          window.open(downloadUrl, '_blank', 'noopener,noreferrer')
          close()
        },
        onCancel: close,
        onClose: close,
      })
    },
  })

  app.mount(container)
}

/** 按失败阶段返回用户可见文案（移动端上传失败单独提示） */
function getExportErrorMessage(err: unknown, opts?: { pdf?: boolean }): string {
  if (getExportDownloadUrl(err)) {
    return 'PPT已生成，自动下载失败，请使用弹窗中的下载按钮'
  }
  if (isExportUploadError(err)) {
    return '上传文件失败，请检查网络后重试'
  }
  if (opts?.pdf) {
    return '生成 PDF 失败，请重试'
  }
  return '生成文件失败，请重试'
}

type ExportUploadFailStage = 'file_sign' | 'oss_put' | 'native_download' | 'network' | 'unknown'

function parseExportUploadFailStage(msg: string): ExportUploadFailStage {
  const detail = msg.replace(EXPORT_UPLOAD_ERROR_MARK, '').trim()
  if (/获取文件签名失败/i.test(detail)) return 'file_sign'
  if (/HTTP状态码/i.test(detail)) return 'oss_put'
  if (/Java exception/i.test(detail)) return 'native_download'
  if (/Network Error|网络异常|Failed to fetch|timeout|ETIMEDOUT/i.test(detail)) return 'network'
  return 'unknown'
}

function getExportUploadBridge(): 'android' | 'ios' | 'miniapp' | 'web' {
  if (isMiniAppWebview()) return 'miniapp'
  const win = window as Window & { JsAndroid?: { downFile?: unknown }; prompt?: (s: string) => string | null }
  if (win.JsAndroid?.downFile) return 'android'
  if (!!win.prompt && !win.JsAndroid?.downFile) return 'ios'
  return 'web'
}

/** 移动端导出上传/原生下载失败埋点（事件名 export_upload_fail） */
function reportExportUploadFail(
  err: unknown,
  ctx: { exportType: string; fileName?: string; blobSize?: number; iosBatchCollect?: boolean },
) {
  if (!isExportUploadError(err)) return
  const msg = err instanceof Error ? err.message : String(err ?? '')
  const detail = msg.replace(EXPORT_UPLOAD_ERROR_MARK, '').trim()
  const payload = {
    stage: parseExportUploadFailStage(msg),
    export_type: ctx.exportType,
    fail_reason: detail.slice(0, 500),
    file_name: (ctx.fileName ?? '').slice(0, 200),
    file_ext: (ctx.fileName?.split('.').pop() ?? '').toUpperCase().slice(0, 16),
    bridge: getExportUploadBridge(),
    ios_batch_collect: !!ctx.iosBatchCollect,
    blob_size_kb: ctx.blobSize != null ? Math.round(ctx.blobSize / 1024) : undefined,
    is_mobile: isMobile(),
  }
  const failDetail = JSON.stringify(payload)
  // App 内 reportEvent → JsAndroid.onEvent，原生侧常只透传 ck_module，自定义事件 params 可能丢失
  reportEvent('export_upload_fail', {
    stage: parseExportUploadFailStage(msg),
    export_type: ctx.exportType,
    fail_reason: detail.slice(0, 500),
    file_name: (ctx.fileName ?? '').slice(0, 200),
    file_ext: (ctx.fileName?.split('.').pop() ?? '').toUpperCase().slice(0, 16),
    bridge: getExportUploadBridge(),
    ios_batch_collect: !!ctx.iosBatchCollect,
    blob_size_kb: ctx.blobSize != null ? Math.round(ctx.blobSize / 1024) : undefined,
    is_mobile: isMobile(),
  })
}

/** iOS defer 导出收集的单条文件信息（上传后 CDN/OSS URL） */
export type IosCollectedExportFile = { fileName: string; fileUrl: string; fileType: string }

const IOS_STITCH_MAX_CANVAS_EDGE = 8192
const IOS_STITCH_GAP_PX = 6
/** 长图 JPEG：偏高码率 */
const IOS_STITCH_JPEG_QUALITY = 0.98

/** 超过此像素数不做锐化，避免长图卡顿 */
const IOS_STITCH_SHARPEN_MAX_PIXELS = 12_000_000

/** 轻度拉普拉斯锐化，提升文字/线条观感，文件体积几乎不变 */
function mildSharpenCanvasRgba(ctx: CanvasRenderingContext2D, w: number, h: number) {
  if (w * h > IOS_STITCH_SHARPEN_MAX_PIXELS || w < 3 || h < 3) return
  const img = ctx.getImageData(0, 0, w, h)
  const d = img.data
  const src = new Uint8ClampedArray(d)
  const w4 = w * 4
  const k = 0.45
  for (let y = 1; y < h - 1; y++) {
    const row = y * w4
    const pr = row - w4
    const nr = row + w4
    for (let x = 1; x < w - 1; x++) {
      const i = row + x * 4
      for (let c = 0; c < 3; c++) {
        const v = src[i + c]
        const lap = v * 4 - src[i - 4 + c] - src[i + 4 + c] - src[pr + x * 4 + c] - src[nr + x * 4 + c]
        d[i + c] = Math.min(255, Math.max(0, Math.round(v + lap * k)))
      }
    }
  }
  ctx.putImageData(img, 0, 0)
}

/**
 * 将多张已上传图片 URL 纵向拼成一张 JPEG（比多次 html-to-image 峰值更可控，但仍会占用：解码位图 + 一张大 canvas）。
 * 若 CDN 未配 CORS，fetch 会失败，调用方应回退为批量 fileList 下载。
 */
async function stitchIosExportedImageRowsToJpegBlob(files: IosCollectedExportFile[]): Promise<Blob> {
  if (!files.length) throw new Error('无图片可拼接')
  const bitmaps: ImageBitmap[] = []
  try {
    for (const f of files) {
      const res = await fetch(f.fileUrl, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
      if (!res.ok) throw new Error(`拉取分段图失败: ${res.status}`)
      bitmaps.push(await createImageBitmap(await res.blob()))
    }
    const maxNatW = Math.max(...bitmaps.map((b) => b.width), 1)
    /** 保持分段图原始像素宽度拼长图，避免此前 1280 上限把高清分段整体缩小导致放大看不清字 */
    const targetW = Math.min(maxNatW, IOS_STITCH_MAX_CANVAS_EDGE)
    const sliceHeights = bitmaps.map((b) => Math.max(1, Math.round((targetW / b.width) * b.height)))
    const totalH =
      sliceHeights.reduce((a, h) => a + h, 0) + IOS_STITCH_GAP_PX * Math.max(0, bitmaps.length - 1)

    let canvas = document.createElement('canvas')
    canvas.width = targetW
    canvas.height = totalH
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas 2D 不可用')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, canvas.width, canvas.height)
    let y = 0
    for (let i = 0; i < bitmaps.length; i++) {
      const b = bitmaps[i]
      const h = sliceHeights[i]
      ctx.drawImage(b, 0, 0, b.width, b.height, 0, y, targetW, h)
      y += h + (i < bitmaps.length - 1 ? IOS_STITCH_GAP_PX : 0)
    }

    if (canvas.width > IOS_STITCH_MAX_CANVAS_EDGE || canvas.height > IOS_STITCH_MAX_CANVAS_EDGE) {
      const s = Math.min(
        IOS_STITCH_MAX_CANVAS_EDGE / canvas.width,
        IOS_STITCH_MAX_CANVAS_EDGE / canvas.height,
      )
      const c2 = document.createElement('canvas')
      c2.width = Math.max(1, Math.floor(canvas.width * s))
      c2.height = Math.max(1, Math.floor(canvas.height * s))
      const ctx2 = c2.getContext('2d')
      if (!ctx2) throw new Error('Canvas 2D 不可用')
      ctx2.imageSmoothingEnabled = true
      ctx2.imageSmoothingQuality = 'high'
      ctx2.drawImage(canvas, 0, 0, c2.width, c2.height)
      canvas = c2
    }

    const encCtx = canvas.getContext('2d')
    if (encCtx) mildSharpenCanvasRgba(encCtx, canvas.width, canvas.height)

    const blob: Blob | null = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', IOS_STITCH_JPEG_QUALITY)
    })
    if (!blob) throw new Error('长图编码失败')
    return blob
  } finally {
    bitmaps.forEach((b) => {
      try {
        b.close()
      } catch {
        /* ignore */
      }
    })
  }
}

export default () => {
  const slidesStore = useSlidesStore()
  const { slides, theme, viewportRatio, title, viewportSize } = storeToRefs(slidesStore)

  const defaultFontSize = 16

  const ratioPx2Inch = computed(() => {
    return 96 * (viewportSize.value / 960)
  })
  const ratioPx2Pt = computed(() => {
    return 96 / 72 * (viewportSize.value / 960)
  })

  const exporting = ref(false)
  let exportCancelled = false

  class ExportCancelledError extends Error {
    constructor() {
      super('Export cancelled')
      this.name = 'ExportCancelledError'
    }
  }

  const isExportCancelled = () => exportCancelled

  const resetExportCancelState = () => {
    exportCancelled = false
  }

  const throwIfExportCancelled = () => {
    if (exportCancelled) throw new ExportCancelledError()
  }

  /** 自定义导出遮罩（与 Element Loading 解耦，避免样式受限） */
  const PPT_FAKE_PROGRESS_DURATION_MS = 25_000
  const PPT_FAKE_PROGRESS_CAP = 95
  /** 窄卡片（min-width 188）内圆环与 viewBox 0..100 匹配 */
  const EXPORT_OVERLAY_SVG_PX = 56
  const EXPORT_RING_R = 32
  const EXPORT_RING_STROKE = 7
  const EXPORT_RING_LEN = 2 * Math.PI * EXPORT_RING_R

  let exportOverlayRoot: HTMLElement | null = null
  let exportOverlayRing: SVGCircleElement | null = null
  let exportOverlayLine1: HTMLElement | null = null
  let exportOverlayLine2: HTMLElement | null = null
  let exportOverlaySvg: SVGSVGElement | null = null
  let exportOverlayProgressBlock: HTMLElement | null = null
  let exportOverlaySuccessBlock: HTMLElement | null = null
  let exportOverlayVariant: 'default' | 'preparing' | 'image' | 'ppt' | 'pdf' | null = null
  let pptFakeProgressTimer: number | null = null
  let pptFakeProgressStart = 0

  type OpenExportLoadingOptions =
    | { variant: 'default' }
    | { variant: 'preparing' }
    | { variant: 'image'; imageTotal: number }
    | { variant: 'ppt' }
    | { variant: 'pdf'; pdfTotal: number }

  const stopPptFakeProgress = () => {
    if (pptFakeProgressTimer) {
      clearInterval(pptFakeProgressTimer)
      pptFakeProgressTimer = null
    }
  }

  const setExportRingPercent = (pct: number) => {
    if (!exportOverlayRing) return
    const p = Math.max(0, Math.min(100, pct))
    const offset = EXPORT_RING_LEN * (1 - p / 100)
    exportOverlayRing.style.strokeDashoffset = String(offset)
  }

  const setExportImageCount = (done: number, total: number) => {
    if (exportOverlayLine2) {
      exportOverlayLine2.textContent = `${done}/${total}`
    }
    if (exportOverlayVariant === 'image' && total > 0) {
      setExportRingPercent((done / total) * 100)
    }
  }

  const setExportPdfProgress = (done: number, total: number) => {
    if (exportOverlayLine1) exportOverlayLine1.textContent = '导出 PDF'
    if (exportOverlayLine2 && total > 0) {
      exportOverlayLine2.textContent = `${done}/${total}`
    }
    if (total > 0) {
      setExportRingPercent((done / total) * 100)
    }
  }

  const ensureYdExportOverlayKeyframes = () => {
    if (typeof document === 'undefined' || document.getElementById('yd-export-overlay-keyframes')) return
    const st = document.createElement('style')
    st.id = 'yd-export-overlay-keyframes'
    st.textContent =
      '@keyframes yd-export-overlay-spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}'
    document.head.appendChild(st)
  }

  const clearExportOverlayLongStripUi = () => {
    if (exportOverlaySvg) {
      exportOverlaySvg.style.animation = ''
      exportOverlaySvg.style.transform = ''
    }
    if (exportOverlayRing) {
      exportOverlayRing.setAttribute('stroke-dasharray', String(EXPORT_RING_LEN))
      exportOverlayRing.style.strokeDashoffset = String(EXPORT_RING_LEN)
    }
  }

  /** iOS 分段截完 → 拼长图 / 上传 阶段：避免仍显示「m/m」却长时间无反馈 */
  const setExportImageLongStripPhase = (phase: 'stitch' | 'upload' | null) => {
    if (!phase) {
      clearExportOverlayLongStripUi()
      if (exportOverlayVariant === 'image' && exportOverlayLine1) {
        exportOverlayLine1.textContent = '下载中'
      }
      return
    }
    if (exportOverlayVariant !== 'image') return
    ensureYdExportOverlayKeyframes()
    if (exportOverlayLine1) exportOverlayLine1.textContent = '合成长图'
    if (exportOverlayLine2) {
      exportOverlayLine2.textContent = phase === 'upload' ? '正在上传长图…' : '合并分段图片中…'
    }
    if (exportOverlaySvg) {
      const half = EXPORT_OVERLAY_SVG_PX / 2
      exportOverlaySvg.style.transformOrigin = `${half}px ${half}px`
      exportOverlaySvg.style.animation = 'yd-export-overlay-spin 0.88s linear infinite'
    }
    if (exportOverlayRing) {
      const dash = Math.max(8, EXPORT_RING_LEN * 0.24)
      exportOverlayRing.setAttribute('stroke-dasharray', `${dash} ${EXPORT_RING_LEN}`)
      exportOverlayRing.style.strokeDashoffset = '0'
    }
  }

  const setExportPptPercent = (pct: number) => {
    if (exportOverlayLine2) {
      exportOverlayLine2.textContent = `${Math.round(pct)}%`
    }
    setExportRingPercent(pct)
  }

  const startPptFakeProgress = () => {
    stopPptFakeProgress()
    pptFakeProgressStart = Date.now()
    setExportPptPercent(0)
    pptFakeProgressTimer = window.setInterval(() => {
      const elapsed = Date.now() - pptFakeProgressStart
      const p = Math.min(PPT_FAKE_PROGRESS_CAP, (elapsed / PPT_FAKE_PROGRESS_DURATION_MS) * PPT_FAKE_PROGRESS_CAP)
      setExportPptPercent(p)
    }, 120)
  }

  const showExportOverlaySuccessThenClose = (delayMs = 1600) => {
    stopPptFakeProgress()
    clearExportOverlayLongStripUi()
    setExportRingPercent(100)
    if (exportOverlayProgressBlock) exportOverlayProgressBlock.style.display = 'none'
    if (exportOverlaySuccessBlock) exportOverlaySuccessBlock.style.display = 'block'
    window.setTimeout(() => {
      closeExportLoading()
    }, delayMs)
  }

  /** 准备阶段：转圈 loading（获取详情等） */
  const setExportPreparingSpin = (active: boolean) => {
    if (!active) {
      clearExportOverlayLongStripUi()
      return
    }
    ensureYdExportOverlayKeyframes()
    if (exportOverlaySvg) {
      const half = EXPORT_OVERLAY_SVG_PX / 2
      exportOverlaySvg.style.transformOrigin = `${half}px ${half}px`
      exportOverlaySvg.style.animation = 'yd-export-overlay-spin 0.88s linear infinite'
    }
    if (exportOverlayRing) {
      const dash = Math.max(8, EXPORT_RING_LEN * 0.24)
      exportOverlayRing.setAttribute('stroke-dasharray', `${dash} ${EXPORT_RING_LEN}`)
      exportOverlayRing.style.strokeDashoffset = '0'
    }
  }

  const openExportLoading = (text = '正在处理文件，请稍候…', opts?: OpenExportLoadingOptions) => {
    const variant = opts?.variant ?? 'default'

    if (exportOverlayRoot) {
      // 准备阶段可被正式导出进度接管：先关掉再重建
      if (
        exportOverlayVariant === 'preparing' &&
        (variant === 'ppt' || variant === 'image' || variant === 'pdf')
      ) {
        closeExportLoading()
      } else if (variant === 'ppt' || variant === 'image' || variant === 'pdf') {
        return
      } else {
        if (exportOverlayLine2 && text) exportOverlayLine2.textContent = text
        if (exportOverlayLine1) {
          exportOverlayLine1.textContent = variant === 'preparing' ? '加载中' : '导出中'
        }
        if (variant === 'preparing') setExportPreparingSpin(true)
        return
      }
    }

    exportOverlayVariant = variant

    const root = document.createElement('div')
    root.className = 'yd-export-overlay'
    /** 必须有不透明底，否则会「看穿」到 z-index:-1 的导出用 DOM（幻灯片像跑出来）；勿动缩略图层 ::after */
    root.style.cssText =
      'position:fixed;inset:0;z-index:30000;display:flex;align-items:center;justify-content:center;'
    const card = document.createElement('div')
    const mobileExportCardWidth = 160
    const mobileExportCardHeight = Math.round((mobileExportCardWidth * 106) / 138)
    card.style.cssText = isMobile()
      ? `position:relative;width:${mobileExportCardWidth}px;height:${mobileExportCardHeight}px;box-sizing:border-box;padding:10px;border-radius:12px;background:rgba(0,0,0,0.8);`
      : 'position:relative;min-width:188px;max-width:65vw;padding:10px 10px 14px;border-radius:12px;background:rgba(0,0,0,0.8);'

    const btnX = document.createElement('button')
    btnX.type = 'button'
    btnX.setAttribute('aria-label', '关闭')
    btnX.textContent = '×'
    btnX.style.cssText =
      'position:absolute;top:8px;right:10px;width:22px;height:22px;border:none;border-radius:50%;background:rgba(255,255,255,0.12);color:#fff;font-size:14px;font-weight:bold;line-height:1;cursor:pointer;display:flex;align-items:center;justify-content:center;padding:0;'
    btnX.onclick = () => {
      cancelExport()
    }

    const progressBlock = document.createElement('div')
    exportOverlayProgressBlock = progressBlock

    const svgNs = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(svgNs, 'svg')
    svg.setAttribute('width', String(EXPORT_OVERLAY_SVG_PX))
    svg.setAttribute('height', String(EXPORT_OVERLAY_SVG_PX))
    svg.setAttribute('viewBox', '0 0 100 100')
    svg.style.display = 'block'
    svg.style.margin = '0 auto'

    const track = document.createElementNS(svgNs, 'circle')
    track.setAttribute('cx', '50')
    track.setAttribute('cy', '50')
    track.setAttribute('r', String(EXPORT_RING_R))
    track.setAttribute('fill', 'none')
    track.setAttribute('stroke', 'rgba(255,255,255,0.22)')
    track.setAttribute('stroke-width', String(EXPORT_RING_STROKE))

    const arc = document.createElementNS(svgNs, 'circle')
    exportOverlayRing = arc
    arc.setAttribute('cx', '50')
    arc.setAttribute('cy', '50')
    arc.setAttribute('r', String(EXPORT_RING_R))
    arc.setAttribute('fill', 'none')
    arc.setAttribute('stroke', '#ffffff')
    arc.setAttribute('stroke-width', String(EXPORT_RING_STROKE))
    arc.setAttribute('stroke-linecap', 'round')
    arc.setAttribute('transform', 'rotate(-90 50 50)')
    arc.setAttribute('stroke-dasharray', String(EXPORT_RING_LEN))
    arc.setAttribute('stroke-dashoffset', String(EXPORT_RING_LEN))

    svg.appendChild(track)
    svg.appendChild(arc)
    exportOverlaySvg = svg as unknown as SVGSVGElement
    progressBlock.appendChild(svg)

    const line1 = document.createElement('div')
    exportOverlayLine1 = line1
    line1.style.cssText = 'margin-top:8px;color:#fff;font-size:13px;font-weight:600;text-align:center;line-height:1.25;'
    line1.textContent = '下载中'

    const line2 = document.createElement('div')
    exportOverlayLine2 = line2
    line2.style.cssText = 'margin-top:6px;color:rgba(255,255,255,0.88);font-size:12px;text-align:center;min-height:18px;line-height:1.3;'
    if (variant === 'image' && opts && 'imageTotal' in opts) {
      line2.textContent = `0/${opts.imageTotal}`
    } else if (variant === 'pdf' && opts && 'pdfTotal' in opts) {
      line1.textContent = '导出 PDF'
      line2.textContent = `0/${opts.pdfTotal}`
    } else if (variant === 'ppt') {
      line2.textContent = '0%'
    } else if (variant === 'preparing') {
      line1.textContent = '加载中'
      line2.textContent = text || '正在获取PPT数据…'
    } else {
      line2.textContent = text
      line1.textContent = '导出中'
    }

    progressBlock.appendChild(line1)
    progressBlock.appendChild(line2)

    const successBlock = document.createElement('div')
    exportOverlaySuccessBlock = successBlock
    successBlock.style.cssText = 'display:none;text-align:center;'
    const okIcon = document.createElement('div')
    okIcon.style.cssText =
      'width:40px;height:40px;margin:0 auto;border-radius:50%;background:#22c55e;color:#fff;font-size:22px;line-height:40px;font-weight:700;'
    okIcon.textContent = '✓'
    const okText = document.createElement('div')
    okText.style.cssText = 'margin-top:10px;color:#fff;font-size:13px;font-weight:600;line-height:1.25;'
    okText.textContent = '下载成功'
    successBlock.appendChild(okIcon)
    successBlock.appendChild(okText)

    // const cancelRow = document.createElement('div')
    // cancelRow.style.cssText = 'margin-top:22px;text-align:center;'
    // const cancelBtn = document.createElement('button')
    // cancelBtn.type = 'button'
    // cancelBtn.textContent = '取消导出'
    // cancelBtn.style.cssText = isMobile()
    //   ? 'width:40%;min-width:120px;margin:0 auto;padding:12px 16px;background:linear-gradient(90deg,#ACB4FF 0%,#707DFF 52.68%,#ACB4FF 100%);color:#fff;border:none;border-radius:12px;cursor:pointer;font-size:14px;'
    //   : 'width:200px;padding:12px 16px;background:linear-gradient(90deg,#ACB4FF 0%,#707DFF 52.68%,#ACB4FF 100%);color:#fff;border:none;border-radius:12px;cursor:pointer;font-size:14px;'
    // cancelBtn.onclick = () => {
    //   closeExportLoading()
    //   ElMessage.warning({ message: '导出已取消', offset: 60 })
    // }
    // cancelRow.appendChild(cancelBtn)

    card.appendChild(btnX)
    card.appendChild(progressBlock)
    card.appendChild(successBlock)
    // card.appendChild(cancelRow)
    root.appendChild(card)
    document.body.appendChild(root)
    exportOverlayRoot = root

    if (variant === 'ppt') {
      startPptFakeProgress()
    } else if (variant === 'image' && opts && 'imageTotal' in opts) {
      setExportRingPercent(0)
    } else if (variant === 'pdf' && opts && 'pdfTotal' in opts) {
      setExportPdfProgress(0, opts.pdfTotal)
    } else if (variant === 'preparing') {
      setExportPreparingSpin(true)
    } else {
      setExportRingPercent(12)
    }
  }

  const closeExportLoading = () => {
    stopPptFakeProgress()
    clearExportOverlayLongStripUi()
    exportOverlayVariant = null
    exportOverlayRing = null
    exportOverlayLine1 = null
    exportOverlayLine2 = null
    exportOverlaySvg = null
    exportOverlayProgressBlock = null
    exportOverlaySuccessBlock = null
    if (exportOverlayRoot) {
      exportOverlayRoot.remove()
      exportOverlayRoot = null
    }
  }

  const cancelExport = () => {
    if (!exporting.value && !exportOverlayRoot) return
    exportCancelled = true
    exporting.value = false
    closeExportLoading()
    ElMessage.warning({
      message: '\u5bfc\u51fa\u5df2\u53d6\u6d88',
      offset: 60,
    })
  }

  /** 在图片右下角添加「内容由AI生成」水印，返回新的 dataUrl */
  const addAIGCWatermarkToImage = (dataUrl: string, format: 'png' | 'jpeg'): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('无法获取 canvas 2d 上下文'))
          return
        }
        ctx.drawImage(img, 0, 0)
        const fontSize = Math.max(20, Math.round(canvas.width / 80))
        ctx.font = `${fontSize}px "Microsoft YaHei", "PingFang SC", sans-serif`
        ctx.fillStyle = 'rgba(0, 0, 0, 1)'
        const text = '内容由AI生成'
        ctx.textAlign = 'right'
        ctx.textBaseline = 'bottom'
        const padding = Math.max(12, Math.round(canvas.width / 100))
        ctx.fillText(text, canvas.width - padding, canvas.height - padding)
        const mime = format === 'png' ? 'image/png' : 'image/jpeg'
        const quality = format === 'png' ? undefined : 0.95
        const result = canvas.toDataURL(mime, quality)
        resolve(result)
      }
      img.onerror = () => reject(new Error('图片加载失败'))
      img.src = dataUrl
    })
  }

  /** AIGC 隐式标识 JSON（符合《人工智能生成合成内容标识办法》） */
  const user = localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user') || '{}') : {}
  const Id = getQueryParam('id') ? getQueryParam('id') : localStorage.getItem('sseId') || ''
  const AIGC_METADATA = JSON.stringify({
    Label: '1',
    ContentProducer: '广州微阿信息技术有限公司一点PPT',
    ProduceID: dayjs().format('YYYYMMDD') + '_' + Id,
    // ReservedCode1: '',
    ContentPropagator: user.userInfo?.shortId || '',
    PropagateID: dayjs().format('YYYYMMDD') + '_' + Id,
    // ReservedCode2: '',
  })

  /** 向 PPTX blob 注入 AIGC 隐式标识（docProps/custom.xml） */
  const injectAIGCMetadataToPPTX = async (blob: Blob): Promise<Blob> => {
    const zip = await JSZip.loadAsync(blob)
    const customXml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/custom-properties" xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">
  <property fmtid="{D5CDD505-2E9C-101B-9397-08002B2CF9AE}" pid="2" name="AIGC">
    <vt:lpwstr>${AIGC_METADATA.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</vt:lpwstr>
  </property>
</Properties>`
    zip.file('docProps/custom.xml', customXml)

    const relsPath = '_rels/.rels'
    let relsXml = await zip.file(relsPath)?.async('string')
    if (!relsXml) return blob
    if (!relsXml.includes('custom.xml')) {
      const ids = relsXml.match(/rId\d+/g) || []
      const maxId = ids.length ? Math.max(...ids.map((s) => parseInt(s.replace('rId', ''), 10))) : 0
      const customRelId = `rId${maxId + 1}`
      const insertBefore = '</Relationships>'
      const newRel = `  <Relationship Id="${customRelId}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/custom-properties" Target="docProps/custom.xml"/>
`
      relsXml = relsXml.replace(insertBefore, newRel + insertBefore)
      zip.file(relsPath, relsXml)
    }

    const ctPath = '[Content_Types].xml'
    let ctXml = await zip.file(ctPath)?.async('string')
    if (ctXml && !ctXml.includes('custom-properties')) {
      const insertBefore = '</Types>'
      const override = `  <Override PartName="/docProps/custom.xml" ContentType="application/vnd.openxmlformats-officedocument.custom-properties+xml"/>
`
      ctXml = ctXml.replace(insertBefore, override + insertBefore)
      zip.file(ctPath, ctXml)
    }

    return zip.generateAsync({ type: 'blob' })
  }

    /** 向 PDF 注入 AIGC 隐式标识（文档 Subject 元数据） */
    const injectAIGCMetadataToPDF = async (blob: Blob): Promise<Blob> => {
      const arrayBuffer = await blob.arrayBuffer()
      const pdfDoc = await PDFDocument.load(arrayBuffer, { ignoreEncryption: true })
      pdfDoc.setSubject(AIGC_METADATA)
      pdfDoc.setKeywords(['AIGC'])
      const pdfBytes = await pdfDoc.save()
      return new Blob([pdfBytes as BlobPart], { type: 'application/pdf' })
    }
  

  /** 移动端：上传文件到服务器并调原生下载（仅用于图片、PPT） */
  /** deferNativeDownload: 仅上传并返回 { fileName, fileUrl, fileType }，不触发原生下载（最后由 iOS fileList 或安卓多次 prompt/downFile） */
  const uploadFileToNative = async (
    blob: Blob,
    fileName: string,
    mimeType: string,
    source: string,
    deferNativeDownload?: boolean,
    trackExportType?: string,
  ): Promise<string | { fileName: string; fileUrl: string; fileType: string }> => {
    throwIfExportCancelled()
    fileName = sanitizeExportFileName(fileName)
    try {
      const userApi = await import('@/api/user')
      const getFileSign = userApi.getFileSign
      const fileExtension = fileName.split('.').pop()?.toUpperCase() || ''
      const signRes = await getFileSign({ params: { fileType: fileExtension, source } }) as { code: string; data: { sign: string; fileUrl: string } }
      if (signRes.code !== 'SUCCESS') {
        throw new Error(`${EXPORT_UPLOAD_ERROR_MARK} 获取文件签名失败`)
      }

      const res = await fetch(signRes.data.sign, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': mimeType },
      })
      if (res.status !== 200) {
        throw new Error(`${EXPORT_UPLOAD_ERROR_MARK} 文件上传失败，HTTP状态码: ${res.status}`)
      }

      throwIfExportCancelled()

      if (!deferNativeDownload) {
        setupDownProgress()
      }

      // 微信小程序 web-view：交给小程序原生页 downloadFile / openDocument / 保存相册
      if (isMiniAppWebview()) {
        if (deferNativeDownload) {
          return { fileName, fileUrl: signRes.data.fileUrl, fileType: fileExtension }
        }
        throwIfExportCancelled()
        requestMiniAppDownload({
          fileName,
          fileUrl: signRes.data.fileUrl,
          fileType: fileExtension,
        })
        return signRes.data.fileUrl
      }

      const win = window as Window & { JsAndroid?: { downFile: (name: string, url: string, type: string) => void }; prompt?: (s: string) => string | null }
      if (win.JsAndroid?.downFile) {
        if (deferNativeDownload) {
          return { fileName, fileUrl: signRes.data.fileUrl, fileType: fileExtension }
        }
        throwIfExportCancelled()
        win.JsAndroid.downFile(fileName, signRes.data.fileUrl, fileExtension)
        return signRes.data.fileUrl
      }
      if (deferNativeDownload) {
        return { fileName, fileUrl: signRes.data.fileUrl, fileType: fileExtension }
      }
      // iOS 单次下载：fileName 带秒级时间戳（App WebView，非小程序）
      if (win.prompt) {
        throwIfExportCancelled()
        const iosFileName = appendTimestampForIosFileName(fileName)
        win.prompt(`ydppt://downFile?fileName=${encodeURIComponent(iosFileName)}&fileUrl=${encodeURIComponent(signRes.data.fileUrl)}&fileType=${encodeURIComponent(fileExtension)}&closeHid=true`)
      }
      return signRes.data.fileUrl
    } catch (e) {
      if (e instanceof ExportCancelledError) throw e
      const toThrow = isExportUploadError(e)
        ? (e instanceof Error ? e : new Error(String(e)))
        : new Error(`${EXPORT_UPLOAD_ERROR_MARK} ${e instanceof Error ? e.message : String(e ?? '') || '网络异常'}`)
      reportExportUploadFail(toThrow, {
        exportType: trackExportType ?? 'mobile_upload',
        fileName,
        blobSize: blob.size,
        deferNativeDownload,
      })
      throw toThrow
    }
  }
  const uploadFilesToIOSBatch = async (files: Array<{ fileName: string; fileUrl: string; fileType: string }>) => {
    throwIfExportCancelled()
    setupDownProgress()
    const win = window as Window & { prompt?: (s: string) => string | null }
    if (win.prompt) {
      try {
        const filesForIos = files.map((f) => ({
          ...f,
          fileName: appendTimestampForIosFileName(f.fileName),
        }))
        console.log('iOS批量下载文件列表:', filesForIos)
        const fileListJson = JSON.stringify(filesForIos)
        win.prompt(`ydppt://downFile?fileList=${encodeURIComponent(fileListJson)}&closeHid=true`)
      } catch (e) {
        console.error('iOS批量下载失败:', e)
      }
    }
  }

  /** iOS：每次往导出 DOM 里塞几页（`getMobileExportImagePageChunk`）；试验开关开启时与安卓同为 4 */
  const getIosExportImagePageChunkSize = () =>
    isIosExportLikeAndroidTestEnabled()
      ? ANDROID_EXPORT_IMAGE_CHUNK_SIZE
      : isIPhone12OrBelow()
        ? 1
        : 3

  /** Android 与 exportImage 内 CHUNK 一致：多页一截 */
  const getAndroidExportImagePageChunkSize = () => ANDROID_EXPORT_IMAGE_CHUNK_SIZE

  const getMobileExportImagePageChunk = (isIosExportImage: boolean) =>
    isIosExportImage ? getIosExportImagePageChunkSize() : getAndroidExportImagePageChunkSize()

  /** 安卓分段已上传：无 fileList，按文件依次 `ydppt://downFile` 单次 prompt（与 iOS 单文件协议一致）；无 prompt 时回退 JsAndroid.downFile */
  const downloadAndroidDeferredFilesSequentialPrompt = (files: IosCollectedExportFile[]) => {
    throwIfExportCancelled()
    const win = window as Window & { JsAndroid?: { downFile: (name: string, url: string, type: string) => void }; prompt?: (s: string) => string | null }
    setupDownProgress()
    for (const f of files) {
      throwIfExportCancelled()
      const ext = (f.fileType || f.fileName.split('.').pop() || 'PNG').toUpperCase()
      const name = appendTimestampForIosFileName(f.fileName)
      if (win.prompt) {
        win.prompt(
          `ydppt://downFile?fileName=${encodeURIComponent(name)}&fileUrl=${encodeURIComponent(f.fileUrl)}&fileType=${encodeURIComponent(ext)}`,
        )
      } else if (win.JsAndroid?.downFile) {
        win.JsAndroid.downFile(name, f.fileUrl, ext)
      }
    }
  }

  /**
   * 分段（多页一张图）已全部上传：iOS 一次 fileList 批量下载；安卓多次 prompt（或 downFile）。
   */
  const finalizeMobileDeferredSegmentDownloads = async (
    files: IosCollectedExportFile[],
    isIosExportImage: boolean,
  ): Promise<void> => {
    throwIfExportCancelled()
    if (!files.length) return
    if (isMiniAppWebview()) {
      await requestMiniAppDownloadSafe(files)
      return
    }
    if (isIosExportImage) {
      await uploadFilesToIOSBatch(files)
    } else {
      downloadAndroidDeferredFilesSequentialPrompt(files)
    }
  }

  /**
   * 移动端（iOS / Android）分段上传后的 URL → 纵向拼 JPEG → 再上传并触发一次原生下载。
   * 失败：iOS 回退 fileList prompt；Android 回退多次 downFile。
   */
  const finalizeMobileDeferredImagesAsLongStrip = async (
    files: IosCollectedExportFile[],
    outputBaseName: string,
  ): Promise<void> => {
    throwIfExportCancelled()
    if (!files.length) return
    const win = window as Window & { JsAndroid?: { downFile: (name: string, url: string, type: string) => void }; prompt?: (s: string) => string | null }
    setExportImageLongStripPhase('stitch')
    try {
      const blob = await stitchIosExportedImageRowsToJpegBlob(files)
      throwIfExportCancelled()
      setExportImageLongStripPhase('upload')
      const safe = sanitizeExportBaseName(outputBaseName).slice(0, 80) || 'export'
      await uploadFileToNative(blob, `${safe}_长图.jpeg`, 'image/jpeg', 'TEMPORARY_FILE', false, 'image_long_strip')
    } catch (e) {
      if (isExportCancelled()) {
        setExportImageLongStripPhase(null)
        return
      }
      console.warn('移动端拼长图失败，回退多文件下载:', e)
      setExportImageLongStripPhase(null)
      if (win.JsAndroid?.downFile) {
        downloadAndroidDeferredFilesSequentialPrompt(files)
      } else {
        await uploadFilesToIOSBatch(files)
      }
    }
  }

  /** 注册 iOS 原生保存进度监听（前端上传完成后由原生 downProgress 回调驱动） */
  const registerNativeSaveProgress = (handler: NativeSaveProgressHandler | null) => {
    nativeSaveProgressHandler = handler
    // 注册监听时立刻挂上 window.downProgress，避免 url 导出等路径漏调 setupDownProgress
    if (handler) setupDownProgress()
  }

  /** 原生下载结果回调（由原生调用） */
  const setupDownProgress = () => {
    const win = window as Window & {
      downProgress?: (status: string, path: string, progress?: number) => void
      __ydpptExportDownProgressInstalled?: boolean
    }
    // 始终指向模块级 handler，避免被其它页面覆盖后失效；允许重复赋值以刷新引用
    win.__ydpptExportDownProgressInstalled = true
    win.downProgress = (...args: unknown[]) => {
      console.log('setupDownProgress', ...args)
      handleNativeDownProgress(args[0], args[1], args[2])
    }
    // 始终绑定最新实现，避免旧回调只 toast、不转发进度
    win.downProgress = (status: string, path: string, progress?: number) => {
      console.log('setupDownProgress', status, path, progress)
      handleNativeDownProgress(status, path, progress)
    }
    win.__ydpptExportDownProgressInstalled = true
  }

  const waitForIframeReady = (iframe: HTMLIFrameElement, timeout = 5000): Promise<void> => {
    return new Promise((resolve) => {
      let done = false
      const finish = () => {
        if (done) return
        done = true
        iframe.removeEventListener('load', finish)
        resolve()
      }

      iframe.addEventListener('load', finish, { once: true })
      window.setTimeout(finish, timeout)

      try {
        const doc = iframe.contentDocument
        if (doc?.readyState === 'complete') window.setTimeout(finish, 0)
      } catch {
        // 跨域 iframe 无法访问 readyState，等待 load/timeout 即可。
      }
    })
  }

  const blobToDataUrl = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = () => reject(reader.error || new Error('Blob 转 DataURL 失败'))
      reader.readAsDataURL(blob)
    })
  }

  const captureIframeViewportToPng = async (iframe: HTMLIFrameElement): Promise<string> => {
    await waitForIframeReady(iframe)
    const width = Math.max(1, Math.ceil(iframe.clientWidth || iframe.offsetWidth || 1))
    const height = Math.max(1, Math.ceil(iframe.clientHeight || iframe.offsetHeight || 1))
    const doc = iframe.contentDocument
    const win = iframe.contentWindow
    if (!doc || !win) throw new Error('HTML iframe 无法访问')

    const target = (doc.body || doc.documentElement) as HTMLElement
    const scrollX = win.scrollX || doc.documentElement.scrollLeft || doc.body?.scrollLeft || 0
    const scrollY = win.scrollY || doc.documentElement.scrollTop || doc.body?.scrollTop || 0
    const savedStyle = {
      width: target.style.width,
      minWidth: target.style.minWidth,
      transform: target.style.transform,
      transformOrigin: target.style.transformOrigin,
    }

    target.style.width = `${Math.max(width, target.scrollWidth || width)}px`
    target.style.minWidth = `${width}px`
    target.style.transform = `translate(${-scrollX}px, ${-scrollY}px)`
    target.style.transformOrigin = 'top left'
    try {
      const blob = await captureDomToBlob(target, {
        width,
        height,
        type: 'image/png',
        pixelRatio: 2,
        backgroundColor: 'transparent',
        embedFonts: false,
      })
      return await blobToDataUrl(blob)
    } finally {
      target.style.width = savedStyle.width
      target.style.minWidth = savedStyle.minWidth
      target.style.transform = savedStyle.transform
      target.style.transformOrigin = savedStyle.transformOrigin
    }
  }

  const injectBaseHrefToHtml = (html: string, src: string): string => {
    const base = `<base href="${src.replace(/"/g, '&quot;')}">`
    if (/<head[^>]*>/i.test(html)) return html.replace(/<head([^>]*)>/i, `<head$1>${base}`)
    return `${base}${html}`
  }

  const captureRemoteHtmlToPng = async (
    src: string,
    width: number,
    height: number,
  ): Promise<string> => {
    const response = await fetch(src, { mode: 'cors', credentials: 'omit', cache: 'no-store' })
    if (!response.ok) throw new Error(`HTML 文件读取失败(${response.status})`)
    const html = await response.text()

    const sandbox = document.createElement('iframe')
    sandbox.style.cssText = [
      'position: fixed',
      'left: -100000px',
      'top: 0',
      `width: ${width}px`,
      `height: ${height}px`,
      'border: 0',
      'opacity: 0',
      'pointer-events: none',
      'background: transparent',
      'z-index: -1',
    ].join(';')
    sandbox.srcdoc = injectBaseHrefToHtml(html, src)
    document.body.appendChild(sandbox)
    try {
      await waitForIframeReady(sandbox)
      await new Promise(resolve => setTimeout(resolve, 300))
      return await captureIframeViewportToPng(sandbox)
    } finally {
      sandbox.remove()
    }
  }

  const prepareHtmlIframeSnapshots = async (root: HTMLElement): Promise<() => void> => {
    const cleanups: Array<() => void> = []
    const iframes = Array.from(root.querySelectorAll('.html-export-content iframe')) as HTMLIFrameElement[]
    for (const iframe of iframes) {
      const width = Math.max(1, Math.ceil(iframe.clientWidth || iframe.offsetWidth || 1))
      const height = Math.max(1, Math.ceil(iframe.clientHeight || iframe.offsetHeight || 1))
      if (width <= 1 || height <= 1) continue

      let dataUrl = ''
      try {
        dataUrl = await captureIframeViewportToPng(iframe)
      } catch (err) {
        try {
          dataUrl = await captureRemoteHtmlToPng(iframe.src, width, height)
        } catch (fallbackErr) {
          console.warn('导出：HTML iframe 截图失败', err, fallbackErr)
        }
      }
      if (!dataUrl) continue

      const img = document.createElement('img')
      img.src = dataUrl
      img.alt = ''
      img.style.cssText = [
        'position: absolute',
        'inset: 0',
        'width: 100%',
        'height: 100%',
        'object-fit: fill',
        'border: 0',
        'pointer-events: none',
      ].join(';')
      const previousVisibility = iframe.style.visibility
      iframe.style.visibility = 'hidden'
      iframe.parentElement?.appendChild(img)
      cleanups.push(() => {
        iframe.style.visibility = previousVisibility
        img.remove()
      })
    }

    return () => cleanups.reverse().forEach(cleanup => cleanup())
  }


  // 导出图片
  // opts.deferBatchNativeDownload: iOS/Android 仅上传收集 URL，由外层 iOS fileList / 安卓多次 prompt 收尾（或单次导出内在下方分支处理）
  // opts.batchMode: 'start' | 'middle' | 'end' 控制 loading：默认单次导出打开/关闭；批量导出只在 start 打开、end 关闭
  const exportImage = async (
    domRef: HTMLElement,
    format: string,
    quality: number,
    ignoreWebfont = true,
    fileName?: string,
    opts?: {
      deferBatchNativeDownload?: boolean
      batchMode?: 'start' | 'middle' | 'end'
      /** 按页导出时整份 PPT 页数，与 imageProgressDone 一起用于「下载中 n/m」 */
      imageProgressTotal?: number
      /** 当前已完成导出的页数（通常为当前页索引 + 1） */
      imageProgressDone?: number
      /** 离屏缩略图实际渲染宽度（与 ThumbnailSlide :size 一致），避免按 store viewportSize 放大导致导出变宽 */
      captureWidth?: number
      disableLoading?: boolean
      onImageProgress?: (done: number, total: number) => void
    },
  ): Promise<void | Array<{ fileName: string; fileUrl: string; fileType: string }>> => {
    if (!domRef) return;
    const mode = opts?.batchMode
    const disableLoading = !!opts?.disableLoading
    const shouldOpenLoading = (!mode || mode === 'start') && !disableLoading
    const shouldCloseLoading = (!mode || mode === 'end') && !disableLoading

    if (!disableLoading && (!mode || mode === 'start')) {
      resetExportCancelState()
      exporting.value = true
      if (isMobile()) {
        const n = domRef.children.length
        const total = opts?.imageProgressTotal ?? Math.max(n, 1)
        openExportLoading('', { variant: 'image', imageTotal: total })
      } else {
        openExportLoading('正在生成长图...', { variant: 'default' })
      }
    } else if (disableLoading && (!mode || mode === 'start')) {
      resetExportCancelState()
      exporting.value = true
    }

    let exportImageHadError = false
    let deferBatchSkipSuccess = false

    // 清理无用属性
    const foreignObjectSpans = domRef.querySelectorAll('foreignObject [xmlns]');
    foreignObjectSpans.forEach(spanRef => spanRef.removeAttribute('xmlns'));
  
    // 确保 DOM 渲染完成
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => setTimeout(resolve, 200));
  
    const finalFileName = sanitizeExportBaseName(fileName || title.value);
    const mimeType = format === 'png' ? 'image/png' : 'image/jpeg';
    const fileExt = format === 'png' ? 'png' : 'jpeg';
  
    try {
      // ============================================================
      // 分支 1：移动端逻辑 (修复版：准确切片 + 高清)
      // ============================================================
      if (isMobile()) {
        let children: HTMLElement[] = []
        try {
        // 【关键修改点 1】使用 children 而不是 querySelectorAll
        children = Array.from(domRef.children) as HTMLElement[];
        const totalSlides = children.length;
  
        const win = window as Window & { JsAndroid?: { downFile: (name: string, url: string, type: string) => void }; prompt?: (s: string) => string | null }
        // 小程序 web-view 也有 prompt，但不能当 App iOS 桥使用
        const isIosExportImage = !isMiniAppWebview() && !!win.prompt && !win.JsAndroid?.downFile;
        // 单次 dom 内按批截图：iPhone 12 及以下默认每批 1 页，其余 iOS 每批 3 页；Android 见 ANDROID_EXPORT_IMAGE_CHUNK_SIZE。
        // 试验：`iosExportLikeAndroid` 时任意 iOS 与安卓同为 4 页一截。
        // 注意：若业务层每次只渲染 1 页到 dom，则此处 CHUNK 不会生效，需配合 getMobileExportImagePageChunk(isIos) 多页渲染。
        const likeAndroidChunk = isIosExportImage && isIosExportLikeAndroidTestEnabled()
        const CHUNK_SIZE = isIosExportImage
          ? likeAndroidChunk
            ? ANDROID_EXPORT_IMAGE_CHUNK_SIZE
            : isIPhone12OrBelow()
              ? 1
              : 3
          : ANDROID_EXPORT_IMAGE_CHUNK_SIZE
        // iOS / Android（defer）上传后仅收集 URL，由外层拼长图再单次原生下载
        const deferredNativeFileList: { fileName: string; fileUrl: string; fileType: string }[] = []
        const deferBatch = !!opts?.deferBatchNativeDownload
  
        children.forEach(el => el.style.display = 'none');
        const chunks = Math.ceil(totalSlides / CHUNK_SIZE);
  
        // setupDownProgress只调用一次
        setupDownProgress()
  
        for (let i = 0; i < chunks; i++) {
          throwIfExportCancelled()
          const start = i * CHUNK_SIZE;
          const end = Math.min((i + 1) * CHUNK_SIZE, totalSlides);
          const currentBatch = children.slice(start, end);
          currentBatch.forEach(el => el.style.display = '');
  
          await new Promise(resolve => requestAnimationFrame(resolve));
          await new Promise(resolve => setTimeout(resolve, 100));
  
          const partName = chunks > 1
            ? `${finalFileName}_${String(i + 1).padStart(2, '0')}.${fileExt}`
            : `${finalFileName}.${fileExt}`;
  
          const batchLen = currentBatch.length
          const useContainerCapture = batchLen > 1
          const targetNode = useContainerCapture
            ? domRef
            : ((currentBatch[0] as HTMLElement | undefined) || domRef)
          /** 输出 width 不得低于实际 DOM 宽度，否则多页截外层容器时只拍到左侧约一半 */
          let minCaptureW = readNodeContentWidthPx(targetNode)
          if (useContainerCapture) {
            minCaptureW = Math.max(minCaptureW, readNodeContentWidthPx(domRef))
            for (const el of currentBatch) {
              if (el instanceof HTMLElement) minCaptureW = Math.max(minCaptureW, readNodeContentWidthPx(el))
            }
          }
          const logicalW = Math.ceil(
            opts?.captureWidth ?? (Number(viewportSize.value) || 960),
          )
          // 以 DOM 实测宽度为准；勿用 store 逻辑宽强行放大（缩略图 :size 可能小于 viewportSize）
          if (minCaptureW < 1) minCaptureW = logicalW
  
          // 少数 PPT 复杂度高时会 OOM 导致 WebView 刷新：这里按配置逐档降级重试
          await new Promise(resolve => setTimeout(resolve, 0))
          const cleanupHtmlSnapshots = await prepareHtmlIframeSnapshots(targetNode)
          let blob: Blob | null = null
          let lastError: any = null
          try {
            for (const step of MOBILE_CAPTURE_STEPS) {
              try {
                const outW = Math.max(step.width, minCaptureW)
                const stepSingleH = outW * viewportRatio.value
                let captureHeight = useContainerCapture ? stepSingleH * batchLen : stepSingleH
                if (useContainerCapture) {
                  await new Promise(resolve => requestAnimationFrame(resolve))
                  const sh = Math.ceil(domRef.scrollHeight)
                  if (sh > 0) captureHeight = Math.max(captureHeight, sh)
                }
                captureHeight = Math.min(captureHeight, MOBILE_CAPTURE_MAX_LOGICAL_H)
                let pixelRatio = getMobileCapturePixelRatio(step, isIosExportImage)
                const edgeCap =
                  !isIosExportImage || isIosExportLikeAndroidTestEnabled()
                    ? MOBILE_EXPORT_MAX_CANVAS_EDGE
                    : isIPhone12OrBelow()
                      ? MOBILE_EXPORT_MAX_CANVAS_EDGE_IOS_LEGACY
                      : MOBILE_EXPORT_MAX_CANVAS_EDGE_IOS_MODERN
                pixelRatio = Math.min(pixelRatio, edgeCap / outW, edgeCap / Math.max(1, captureHeight))
                pixelRatio = Math.max(1, pixelRatio)
                const exportDebug = isMobileExportDebugEnabled()
                const blobOpts = {
                  width: outW,
                  height: captureHeight,
                  quality: step.quality,
                  pixelRatio,
                  type: mimeType as 'image/jpeg' | 'image/png',
                  backgroundColor: '#ffffff',
                  cacheBust: false,
                  style: { transform: 'scale(1)', transformOrigin: 'top left' as const },
                  embedFonts: false,
                }
                if (exportDebug) {
                  // modern-screenshot 无 onImageErrorHandler；调试时仍输出 DOM 快照
                  logMobileExportDebug('pre_domToBlob', {
                    ...snapshotDomForExportDebug(targetNode),
                    partName,
                    batchRange: [start, end],
                    useContainerCapture,
                    outW,
                    captureHeight,
                    pixelRatio,
                    edgeCap,
                    canvasPx: {
                      w: Math.round(outW * pixelRatio),
                      h: Math.round(captureHeight * pixelRatio),
                    },
                    ua: navigator.userAgent?.slice(0, 200),
                    isIosExportImage,
                    isIPhone12OrBelow: isIPhone12OrBelow(),
                    iosExportLikeAndroid: isIosExportLikeAndroidTestEnabled(),
                    stepWidth: step.width,
                  })
                }
                blob = await captureDomToBlob(targetNode, blobOpts)
                if (exportDebug && blob) {
                  logMobileExportDebug('domToBlob_done', {
                    partName,
                    blobBytes: blob.size,
                    blobType: blob.type,
                  })
                  if (blob.size < 8000) {
                    console.warn('[exportDebug] blob 体积异常小，可能接近空图/全黑，请对照 pre_toBlob 的 canvasPx 与 imgSamples')
                  }
                }
                if (blob) break
              } catch (err) {
                lastError = err
                blob = null
                if (isMobileExportDebugEnabled()) {
                  logMobileExportDebug('domToBlob_catch', {
                    partName,
                    message: err instanceof Error ? err.message : String(err),
                    stack: err instanceof Error ? err.stack?.slice(0, 500) : undefined,
                  })
                }
                await new Promise(resolve => requestAnimationFrame(resolve))
                await new Promise(resolve => setTimeout(resolve, 30))
              }
            }
          } finally {
            cleanupHtmlSnapshots()
          }
          if (!blob && lastError) throw lastError;
  
          if (blob) {
            throwIfExportCancelled()
            const result = await uploadFileToNative(blob, partName, mimeType, 'TEMPORARY_FILE', deferBatch, 'image')
            if (deferBatch && typeof result === 'object' && result !== null && 'fileUrl' in result) {
              deferredNativeFileList.push(result)
            }
            blob = null
          }

          const progressTotal = opts?.imageProgressTotal ?? totalSlides
          const progressDone = opts?.imageProgressDone != null ? opts.imageProgressDone : end
          if (opts?.onImageProgress) {
            opts.onImageProgress(progressDone, progressTotal)
          } else if (exportOverlayRoot) {
            setExportImageCount(progressDone, progressTotal)
          }

          currentBatch.forEach(el => {
            el.style.display = 'none'
            el.style.visibility = ''
          });
          
          await new Promise(resolve => setTimeout(resolve, 250));
          // 主动断开节点与布局，帮助 iOS WebView 释放位图缓存（多页截 domRef 时勿隐藏整个容器）
          currentBatch.forEach((el) => {
            try {
              el.style.visibility = 'hidden'
            } catch {}
          })
          await new Promise(resolve => setTimeout(resolve, 0))
          
          if (typeof window !== 'undefined' && 'gc' in window) {
            try {
              (window as any).gc();
            } catch (e) {
            }
          }
        }
  
        const canDeferFinalize = !!(
          win.JsAndroid?.downFile ||
          isIosExportImage ||
          isMiniAppWebview()
        )
        if (canDeferFinalize && deferredNativeFileList.length > 0) {
          throwIfExportCancelled()
          if (opts?.deferBatchNativeDownload) {
            deferBatchSkipSuccess = true
            return deferredNativeFileList
          }
          await finalizeMobileDeferredSegmentDownloads(deferredNativeFileList, isIosExportImage)
        }
  
        } finally {
          children.forEach((el) => {
            el.style.display = ''
            el.style.visibility = ''
          })
        }
      } 
      // ============================================================
      // 分支 2：Web/PC 端逻辑 (保持不变)
      // ============================================================
      else {
        throwIfExportCancelled()
        const fullName = `${finalFileName}.${fileExt}`;
        const captureW = Math.max(
          1,
          opts?.captureWidth ?? readNodeContentWidthPx(domRef) ?? Math.ceil(Number(viewportSize.value) || 1600),
        )
        const prevDomW = domRef.style.width
        const prevDomMinW = domRef.style.minWidth
        domRef.style.width = `${captureW}px`
        domRef.style.minWidth = `${captureW}px`
        await new Promise(resolve => requestAnimationFrame(resolve))
        const cleanupHtmlSnapshots = await prepareHtmlIframeSnapshots(domRef)
        let blob: Blob | null = null
        try {
          blob = await captureDomToBlob(domRef, {
            quality: quality || 0.9,
            width: captureW,
            height: domRef.scrollHeight,
            pixelRatio: 1,
            type: mimeType as 'image/jpeg' | 'image/png',
            backgroundColor: '#ffffff',
            style: { transform: 'scale(1)', transformOrigin: 'top left' },
            embedFonts: !ignoreWebfont,
          })
        } finally {
          cleanupHtmlSnapshots()
          domRef.style.width = prevDomW
          domRef.style.minWidth = prevDomMinW
        }
        if (!blob) throw new Error('生成图片数据为空');
        throwIfExportCancelled()
        saveAs(blob, fullName);
      }
  
    } catch (e) {
      if (!isExportCancelled()) {
        console.error('导出图片失败:', e);
        exportImageHadError = true
        ElMessage.error({ message: getExportErrorMessage(e), offset: 60 });
      }
    } finally {
      if (disableLoading && (!mode || mode === 'end')) {
        exporting.value = false
      } else if (shouldCloseLoading) {
        exporting.value = false;
        if (deferBatchSkipSuccess) {
          // 保持遮罩，外层在 finalizeMobileDeferredSegmentDownloads 后调用 showExportDownloadSuccess
        } else if (!exportImageHadError && exportOverlayRoot) {
          showExportOverlaySuccessThenClose()
        } else {
          closeExportLoading();
        }
      }
      
      // 【兜底恢复】：无论成功失败，必须确保所有元素恢复显示
      if (isMobile() && domRef) {
         const children = Array.from(domRef.children) as HTMLElement[];
         children.forEach(el => el.style.display = '');
         // 恢复高度，避免影响后续渲染/导出
         domRef.style.height = ''
         domRef.style.minHeight = ''
      }
    }
  }


  const pxToMm = (px: number) => (px * 25.4) / 96

  const PDF_PAGE_W_MM = 450



  const trimPdfSlideImage = (dataUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.onload = () => {
        const fullW = img.width
        const fullH = img.height
        if (fullW < 2 || fullH < 2) {
          resolve(dataUrl)
          return
        }

        const useFastDetect = isMobile() && fullW * fullH > 1_500_000
        const detectScale = useFastDetect ? 0.5 : 1
        const detectW = Math.max(1, Math.round(fullW * detectScale))
        const detectH = Math.max(1, Math.round(fullH * detectScale))

        const detectCanvas = document.createElement('canvas')
        detectCanvas.width = detectW
        detectCanvas.height = detectH
        const detectCtx = detectCanvas.getContext('2d')
        if (!detectCtx) {
          resolve(dataUrl)
          return
        }
        detectCtx.drawImage(img, 0, 0, detectW, detectH)
        const { data, width, height } = detectCtx.getImageData(0, 0, detectW, detectH)
        const threshold = 248
        const isNearWhite = (x: number, y: number) => {
          const i = (y * width + x) * 4
          const a = data[i + 3]
          if (a < 8) return true
          return data[i] >= threshold && data[i + 1] >= threshold && data[i + 2] >= threshold
        }
        let top = 0
        let left = 0
        let right = width - 1
        let bottom = height - 1
        outer: for (let y = 0; y < height; y++) {
          for (let x = 0; x < width; x++) {
            if (!isNearWhite(x, y)) { top = y; break outer }
          }
        }
        outer: for (let y = height - 1; y >= top; y--) {
          for (let x = 0; x < width; x++) {
            if (!isNearWhite(x, y)) { bottom = y; break outer }
          }
        }
        outer: for (let x = 0; x < width; x++) {
          for (let y = top; y <= bottom; y++) {
            if (!isNearWhite(x, y)) { left = x; break outer }
          }
        }
        outer: for (let x = width - 1; x >= left; x--) {
          for (let y = top; y <= bottom; y++) {
            if (!isNearWhite(x, y)) { right = x; break outer }
          }
        }

        const scaleBack = (v: number, max: number) => Math.min(max, Math.max(0, Math.floor(v / detectScale)))
        const cropLeft = scaleBack(left, fullW - 1)
        const cropTop = scaleBack(top, fullH - 1)
        const cropRight = Math.min(fullW - 1, Math.ceil((right + 1) / detectScale) - 1)
        const cropBottom = Math.min(fullH - 1, Math.ceil((bottom + 1) / detectScale) - 1)
        const cropW = cropRight - cropLeft + 1
        const cropH = cropBottom - cropTop + 1
        if (cropW < 2 || cropH < 2 || (cropW === fullW && cropH === fullH)) {
          resolve(dataUrl)
          return
        }
        const out = document.createElement('canvas')
        out.width = cropW
        out.height = cropH
        const outCtx = out.getContext('2d')
        if (!outCtx) {
          resolve(dataUrl)
          return
        }
        outCtx.drawImage(img, cropLeft, cropTop, cropW, cropH, 0, 0, cropW, cropH)
        resolve(out.toDataURL('image/jpeg', 0.95))
      }
      img.onerror = () => reject(new Error('trim image load failed'))
      img.src = dataUrl
    })
  }

  const placePdfImageCentered = (
    pdf: jsPDF,
    dataUrl: string,
    pageWmm: number,
    pageHmm: number,
    marginMm: number,
  ) => {
    const { width: imgW, height: imgH } = pdf.getImageProperties(dataUrl)
    const contentWmm = pageWmm - 2 * marginMm
    const contentHmm = pageHmm - 2 * marginMm
    if (!imgW || !imgH) {
      pdf.addImage(dataUrl, 'JPEG', marginMm, marginMm, contentWmm, contentHmm, undefined, 'SLOW')
      return
    }
    const imgAspect = imgW / imgH
    const boxAspect = contentWmm / contentHmm
    let drawWmm: number
    let drawHmm: number
    if (imgAspect >= boxAspect) {
      drawWmm = contentWmm
      drawHmm = contentWmm / imgAspect
    } else {
      drawHmm = contentHmm
      drawWmm = contentHmm * imgAspect
    }
    const drawX = marginMm + (contentWmm - drawWmm) / 2
    const drawY = marginMm + (contentHmm - drawHmm) / 2
    pdf.addImage(dataUrl, 'JPEG', drawX, drawY, drawWmm, drawHmm, undefined, 'SLOW')
  }

  const capturePdfSlideToJpeg = (
    node: HTMLElement,
    options: {
      width: number
      height: number
      quality: number
      pixelRatio: number
      backgroundColor: string
      cacheBust: boolean
    },
  ): Promise<string> => captureDomToJpeg(node, {
    width: options.width,
    height: options.height,
    quality: options.quality,
    pixelRatio: options.pixelRatio,
    backgroundColor: options.backgroundColor,
    cacheBust: options.cacheBust,
    embedFonts: false,
  })

  const exportPDF = async (
    domRef: HTMLElement,
    pageSize: ExportPDFPageSize,
    fileName?: string,
    options?: ExportPDFOptions,
  ): Promise<Blob | void> => {
    if (!domRef) return

    const slidesPerPage = isMobile() ? 1 : Math.max(1, options?.slidesPerPage ?? 1)
    const { width, height, margin } = pageSize
    const slideHeight = height / slidesPerPage

    const disableLoading = !!options?.disableLoading
    const isBatchPdfPart = !!options?.batchPdfPart
    if (!isBatchPdfPart) {
      resetExportCancelState()
      exporting.value = true
    }

    const children = Array.from(domRef.children) as HTMLElement[]
    const originalDisplays = children.map(el => el.style.display)
    const prevContainerWidth = domRef.style.width
    const prevContainerMinWidth = domRef.style.minWidth
    const prevDomDisplay = domRef.style.display
    const prevDomFlexDirection = domRef.style.flexDirection
    const prevDomAlignItems = domRef.style.alignItems

    let pdf: jsPDF | null = null

    try {
      if (children.length === 0) {
        throw new Error('没有可导出的幻灯片')
      }

      domRef.style.width = `${width}px`
      domRef.style.minWidth = `${width}px`

      const totalPages = Math.ceil(children.length / slidesPerPage)
      if (!disableLoading) {
        openExportLoading('', { variant: 'pdf', pdfTotal: totalPages })
      }
      const pdfMarginPx = isMobile() ? 0 : margin
      const marginMm = pxToMm(pdfMarginPx)

      for (let pageIndex = 0; pageIndex < totalPages; pageIndex++) {
        throwIfExportCancelled()
        const pdfProgress = Math.round(((pageIndex + 1) / totalPages) * 100)
        if (options?.onProgress) {
          options.onProgress(pdfProgress)
        } else {
          setExportPdfProgress(pageIndex + 1, totalPages)
        }

        const batch = children.slice(
          pageIndex * slidesPerPage,
          (pageIndex + 1) * slidesPerPage,
        )
        const pageContentH = slideHeight * batch.length

        children.forEach(el => { el.style.display = 'none' })
        batch.forEach(el => { el.style.display = '' })

        await new Promise(resolve => requestAnimationFrame(resolve))
        await new Promise(resolve => setTimeout(resolve, isMobile() ? 350 : 80))

        const rawCaptureTarget = (batch.length === 1 ? batch[0] : domRef) as HTMLElement
        const captureTarget = (rawCaptureTarget.querySelector('.thumbnail-slide') as HTMLElement) || rawCaptureTarget
        const foreignObjectSpans = captureTarget.querySelectorAll('foreignObject [xmlns]')
        foreignObjectSpans.forEach(spanRef => spanRef.removeAttribute('xmlns'))

        const slideCaptureH = Math.round(width * (pageContentH / width))
        const savedCaptureStyle = {
          width: captureTarget.style.width,
          height: captureTarget.style.height,
          margin: captureTarget.style.margin,
          borderRadius: captureTarget.style.borderRadius,
          display: captureTarget.style.display,
        }
        captureTarget.style.width = `${width}px`
        captureTarget.style.height = `${slideCaptureH}px`
        captureTarget.style.margin = '0'
        captureTarget.style.borderRadius = '0'
        captureTarget.style.display = 'block'
        domRef.style.display = 'flex'
        domRef.style.flexDirection = 'column'
        domRef.style.alignItems = 'center'

        const captureSteps = isMobile()
          ? getMobilePdfCaptureSteps()
          : [{ width, quality: 0.95, pixelRatio: 2 }]
        const win = window as Window & { JsAndroid?: { downFile: (name: string, url: string, type: string) => void }; prompt?: (s: string) => string | null }
        const isIosPdfExport = isMobile() && !isMiniAppWebview() && !!win.prompt && !win.JsAndroid?.downFile
        let dataUrl: string | null = null
        let lastCaptureError: unknown = null

        const cleanupHtmlSnapshots = await prepareHtmlIframeSnapshots(captureTarget)
        try {
          for (const step of captureSteps) {
            try {
              const jpeg = await capturePdfSlideToJpeg(captureTarget, {
                width,
                height: slideCaptureH,
                quality: step.quality,
                pixelRatio: isMobile()
                  ? getMobilePdfCapturePixelRatio(step, width, slideCaptureH, isIosPdfExport)
                  : step.pixelRatio,
                backgroundColor: '#ffffff',
                cacheBust: isIosPdfExport,
              })
              const finalJpeg = isIosPdfExport ? jpeg : await trimPdfSlideImage(jpeg)
              const watermarkThisPage = pageIndex === 0 && options?.applyPdfWatermark !== false
              dataUrl = watermarkThisPage
                ? await addAIGCWatermarkToImage(finalJpeg, 'jpeg')
                : finalJpeg
              break
            } catch (err) {
              lastCaptureError = err
              await yieldMobileExportGc(40)
            }
          }
        } finally {
          cleanupHtmlSnapshots()
        }

        if (!dataUrl) throw lastCaptureError ?? new Error('幻灯片截图失败')

        const pageWmm = PDF_PAGE_W_MM
        const pageHmm = pageWmm * (pageContentH / width)
        if (!pdf) {
          pdf = new jsPDF({
            orientation: 'l',
            unit: 'mm',
            format: [pageWmm, pageHmm],
            compress: true,
          })
        } else {
          pdf.addPage([pageWmm, pageHmm], 'l')
        }

        placePdfImageCentered(pdf, dataUrl, pageWmm, pageHmm, marginMm)
        dataUrl = null

        captureTarget.style.width = savedCaptureStyle.width
        captureTarget.style.height = savedCaptureStyle.height
        captureTarget.style.margin = savedCaptureStyle.margin
        captureTarget.style.borderRadius = savedCaptureStyle.borderRadius
        captureTarget.style.display = savedCaptureStyle.display

        batch.forEach(el => { el.style.display = 'none' })
        if (isMobile()) {
          await yieldMobileExportGc(120)
        }
      }

      if (!pdf) throw new Error('PDF 生成失败')

      throwIfExportCancelled()

      const fn = `${sanitizeExportBaseName(fileName || title.value)}.pdf`
      let pdfBlob = pdf.output('blob')
      if (!options?.skipAigcMetadata) {
        pdfBlob = await injectAIGCMetadataToPDF(pdfBlob)
      }

      if (options?.outputBlobOnly) {
        return pdfBlob
      }

      if (isMobile()) {
        await uploadFileToNative(pdfBlob, fn, 'application/pdf', 'TEMPORARY_FILE', undefined, 'pdf')
      } else {
        saveAs(pdfBlob, fn)
        ElMessage.success({ message: 'PDF 导出成功', offset: 60 })
      }
    } catch (e) {
      if (!isExportCancelled()) {
        console.error('导出 PDF 失败:', e)
        ElMessage.error({ message: getExportErrorMessage(e, { pdf: true }), offset: 60 })
      }
    } finally {
      if (!isBatchPdfPart) {
        exporting.value = false
      }
      if (!disableLoading) {
        closeExportLoading()
      }
      children.forEach((el, i) => {
        el.style.display = originalDisplays[i] ?? ''
      })
      domRef.style.width = prevContainerWidth
      domRef.style.minWidth = prevContainerMinWidth
      domRef.style.display = prevDomDisplay
      domRef.style.flexDirection = prevDomFlexDirection
      domRef.style.alignItems = prevDomAlignItems
    }
  }

  // 导出图片版PPTX
  const exportImagePPTX = (domRefs: NodeListOf<Element>) => {
    resetExportCancelState()
    openExportLoading('', { variant: 'ppt' })
    exporting.value = true

    setTimeout(() => {
      const pptx = new pptxgen()

      const config: ExportImageConfig = {
        quality: 1,
        width: 1600,
      }

      const promiseArr = []
      for (const domRef of domRefs) {
        const foreignObjectSpans = domRef.querySelectorAll('foreignObject [xmlns]')
        foreignObjectSpans.forEach(spanRef => spanRef.removeAttribute('xmlns'))

        const promiseFunc = () => captureDomToJpeg((domRef as HTMLElement), {
          width: config.width,
          quality: config.quality,
          embedFonts: false,
        })
        promiseArr.push(promiseFunc)
      }

      Promise.all(promiseArr.map(func => func()))
        .then((imgs) => {
          if (isExportCancelled()) return Promise.reject(new ExportCancelledError())
          return Promise.all(imgs.map((data) => addAIGCWatermarkToImage(data, 'jpeg')))
        })
        .then(async (imgsWithWatermark) => {
          if (isExportCancelled()) return
          try {
            for (const data of imgsWithWatermark) {
              throwIfExportCancelled()
              const pptxSlide = pptx.addSlide()
              pptxSlide.addImage({
                data,
                x: 0,
                y: 0,
                w: viewportSize.value / ratioPx2Inch.value,
                h: viewportSize.value * viewportRatio.value / ratioPx2Inch.value,
              })
            }
            throwIfExportCancelled()
            let blob = await pptx.write({ outputType: 'blob' }) as Blob
            blob = await injectAIGCMetadataToPPTX(blob)
            const fn = `${sanitizeExportBaseName(title.value)}.pptx`
            if (isMobile()) {
              await uploadFileToNative(blob, fn, 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'TEMPORARY_FILE', undefined, 'image_pptx')
            } else {
              saveAs(blob, fn)
            }
            exporting.value = false
            stopPptFakeProgress()
            setExportPptPercent(100)
            showExportOverlaySuccessThenClose()
          } catch (e) {
            console.warn(isExportUploadError(e) ? '上传图片版 PPTX 失败:' : '导出图片版 PPTX 失败:', e)
            exporting.value = false
            if (!isExportCancelled()) {
              ElMessage.error({ message: getExportErrorMessage(e), offset: 60 })
            }
            closeExportLoading()
          }
        })
        .catch((e) => {
          exporting.value = false
          if (!isExportCancelled()) {
            console.warn('导出图片版 PPTX 失败:', e)
            ElMessage.error({ message: getExportErrorMessage(e), offset: 60 })
          }
          closeExportLoading()
        })
    }, 200)
  }
  
  // 导出pptist文件（特有 .pptist 后缀文件）
  const exportSpecificFile = (_slides: Slide[]) => {
    const json = {
      title: title.value,
      width: viewportSize.value,
      height: viewportSize.value * viewportRatio.value,
      theme: theme.value,
      slides: _slides,
    }
    const blob = new Blob([encrypt(JSON.stringify(json))], { type: '' })
    saveAs(blob, `${title.value}.pptist`)
  }
  
  // 导出JSON文件
  const exportJSON = () => {
    const json = {
      title: title.value,
      width: viewportSize.value,
      height: viewportSize.value * viewportRatio.value,
      theme: theme.value,
      slides: slides.value,
    }
    const blob = new Blob([JSON.stringify(json)], { type: '' })
    saveAs(blob, `${title.value}.json`)
  }

  // 格式化颜色值为 透明度 + HexString，供pptxgenjs使用
  const formatColor = (_color: string) => {
    if (!_color) {
      return {
        alpha: 0,
        color: '#000000',
      }
    }

    const c = tinycolor(_color)
    const alpha = c.getAlpha()
    const color = alpha === 0 ? '#ffffff' : c.setAlpha(1).toHexString()
    return {
      alpha,
      color,
    }
  }

  type FormatColor = ReturnType<typeof formatColor>

  // 将HTML字符串格式化为pptxgenjs所需的格式
  // 核心思路：将HTML字符串按样式分片平铺，每个片段需要继承祖先元素的样式信息，遇到块级元素需要换行
  const formatHTML = (html: string) => {
    const ast = toAST(html)
    let bulletFlag = false
    let indent = 0

    // 将 CSS font-size 转为 pptxgen 可用的 pt
    // - 编辑器里元素使用的是“画布像素”，因此 px→pt 需按当前画布 px/pt 比例换算（ratioPx2Pt）
    // - 兼容 pt：直接使用
    const parseFontSizeToPt = (raw: string | undefined): number | undefined => {
      if (!raw) return undefined
      const v = String(raw).trim().toLowerCase()
      const n = Number.parseFloat(v)
      if (!Number.isFinite(n) || n <= 0) return undefined
      if (v.endsWith('pt')) return n
      // 默认按 px（也兼容纯数字/px）
      return n / ratioPx2Pt.value
    }

    const slices: pptxgen.TextProps[] = []
    const parse = (obj: AST[], baseStyleObj: { [key: string]: string } = {}) => {

      for (const item of obj) {
        const isBlockTag = 'tagName' in item && ['div', 'li', 'p'].includes(item.tagName)

        if (isBlockTag && slices.length) {
          const lastSlice = slices[slices.length - 1]
          if (!lastSlice.options) lastSlice.options = {}
          lastSlice.options.breakLine = true
        }

        const styleObj = { ...baseStyleObj }
        const styleAttr = 'attributes' in item ? item.attributes.find(attr => attr.key === 'style') : null
        if (styleAttr && styleAttr.value) {
          const styleArr = styleAttr.value.split(';')
          for (const styleItem of styleArr) {
            const match = styleItem.match(/([^:]+):\s*(.+)/)
            if (match) {
              const [key, value] = [match[1].trim(), match[2].trim()]
              if (key && value) styleObj[key] = value
            }
          }
        }

        if ('tagName' in item) {
          if (item.tagName === 'em') {
            styleObj['font-style'] = 'italic'
          }
          if (item.tagName === 'strong') {
            styleObj['font-weight'] = 'bold'
          }
          if (item.tagName === 'sup') {
            styleObj['vertical-align'] = 'super'
          }
          if (item.tagName === 'sub') {
            styleObj['vertical-align'] = 'sub'
          }
          if (item.tagName === 'a') {
            const attr = item.attributes.find(attr => attr.key === 'href')
            styleObj['href'] = attr?.value || ''
          }
          if (item.tagName === 'ul') {
            styleObj['list-type'] = 'ul'
          }
          if (item.tagName === 'ol') {
            styleObj['list-type'] = 'ol'
          }
          if (item.tagName === 'li') {
            bulletFlag = true
          }
          if (item.tagName === 'p') {
            if ('attributes' in item) {
              const dataIndentAttr = item.attributes.find(attr => attr.key === 'data-indent')
              if (dataIndentAttr && dataIndentAttr.value) indent = +dataIndentAttr.value
            }
          }
        }

        if ('tagName' in item && item.tagName === 'br') {
          slices.push({ text: '', options: { breakLine: true } })
        }
        else if ('content' in item) {
          const text = item.content.replace(/&nbsp;/g, ' ').replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&amp;/g, '&').replace(/\n/g, '')
          const options: pptxgen.TextPropsOptions = {}

          if (styleObj['font-size']) {
            const fs = parseFontSizeToPt(styleObj['font-size'])
            if (fs) options.fontSize = fs
          }
          if (styleObj['color']) {
            options.color = formatColor(styleObj['color']).color
          }
          if (styleObj['background-color']) {
            options.highlight = formatColor(styleObj['background-color']).color
          }
          if (styleObj['text-decoration-line']) {
            if (styleObj['text-decoration-line'].indexOf('underline') !== -1) {
              options.underline = {
                color: options.color || '#000000',
                style: 'sng',
              }
            }
            if (styleObj['text-decoration-line'].indexOf('line-through') !== -1) {
              options.strike = 'sngStrike'
            }
          }
          if (styleObj['text-decoration']) {
            if (styleObj['text-decoration'].indexOf('underline') !== -1) {
              options.underline = {
                color: options.color || '#000000',
                style: 'sng',
              }
            }
            if (styleObj['text-decoration'].indexOf('line-through') !== -1) {
              options.strike = 'sngStrike'
            }
          }
          if (styleObj['vertical-align']) {
            if (styleObj['vertical-align'] === 'super') options.superscript = true
            if (styleObj['vertical-align'] === 'sub') options.subscript = true
          }
          if (styleObj['text-align']) options.align = styleObj['text-align'] as pptxgen.HAlign
          if (styleObj['font-weight']) options.bold = styleObj['font-weight'] === 'bold'
          if (styleObj['font-style']) options.italic = styleObj['font-style'] === 'italic'
          if (styleObj['font-family']) options.fontFace = styleObj['font-family']
          if (styleObj['href']) options.hyperlink = { url: styleObj['href'] }

          if (bulletFlag && styleObj['list-type'] === 'ol') {
            options.bullet = { type: 'number', indent: (options.fontSize || defaultFontSize) * 1.25 }
            options.paraSpaceBefore = 0.1
            bulletFlag = false
          }
          if (bulletFlag && styleObj['list-type'] === 'ul') {
            options.bullet = { indent: (options.fontSize || defaultFontSize) * 1.25 }
            options.paraSpaceBefore = 0.1
            bulletFlag = false
          }
          if (indent) {
            options.indentLevel = indent
            indent = 0
          }

          slices.push({ text, options })
        }
        else if ('children' in item) parse(item.children, styleObj)
      }
    }
    parse(ast)
    return slices
  }

  type Points = Array<
    | { x: number; y: number; moveTo?: boolean }
    | { x: number; y: number; curve: { type: 'arc'; hR: number; wR: number; stAng: number; swAng: number } }
    | { x: number; y: number; curve: { type: 'quadratic'; x1: number; y1: number } }
    | { x: number; y: number; curve: { type: 'cubic'; x1: number; y1: number; x2: number; y2: number } }
    | { close: true }
  >

  // 将SVG路径信息格式化为pptxgenjs所需要的格式
  const formatPoints = (points: SvgPoints, scale = { x: 1, y: 1 }): Points => {
    return points.map(point => {
      if (point.close !== undefined) {
        return { close: true }
      }
      else if (point.type === 'M') {
        return {
          x: point.x / ratioPx2Inch.value * scale.x,
          y: point.y / ratioPx2Inch.value * scale.y,
          moveTo: true,
        }
      }
      else if (point.curve) {
        if (point.curve.type === 'cubic') {
          return {
            x: point.x / ratioPx2Inch.value * scale.x,
            y: point.y / ratioPx2Inch.value * scale.y,
            curve: {
              type: 'cubic',
              x1: (point.curve.x1 as number) / ratioPx2Inch.value * scale.x,
              y1: (point.curve.y1 as number) / ratioPx2Inch.value * scale.y,
              x2: (point.curve.x2 as number) / ratioPx2Inch.value * scale.x,
              y2: (point.curve.y2 as number) / ratioPx2Inch.value * scale.y,
            },
          }
        }
        else if (point.curve.type === 'quadratic') {
          return {
            x: point.x / ratioPx2Inch.value * scale.x,
            y: point.y / ratioPx2Inch.value * scale.y,
            curve: {
              type: 'quadratic',
              x1: (point.curve.x1 as number) / ratioPx2Inch.value * scale.x,
              y1: (point.curve.y1 as number) / ratioPx2Inch.value * scale.y,
            },
          }
        }
      }
      return {
        x: point.x / ratioPx2Inch.value * scale.x,
        y: point.y / ratioPx2Inch.value * scale.y,
      }
    })
  }

  // 获取阴影配置
  const getShadowOption = (shadow: PPTElementShadow): pptxgen.ShadowProps => {
    const c = formatColor(shadow.color)
    const { h, v } = shadow

    let offset = 4
    let angle = 45

    if (h === 0 && v === 0) {
      offset = 4
      angle = 45
    }
    else if (h === 0) {
      if (v > 0) {
        offset = v
        angle = 90
      }
      else {
        offset = -v
        angle = 270
      }
    }
    else if (v === 0) {
      if (h > 0) {
        offset = h
        angle = 1
      }
      else {
        offset = -h
        angle = 180
      }
    }
    else if (h > 0 && v > 0) {
      offset = Math.max(h, v)
      angle = 45
    }
    else if (h > 0 && v < 0) {
      offset = Math.max(h, -v)
      angle = 315
    }
    else if (h < 0 && v > 0) {
      offset = Math.max(-h, v)
      angle = 135
    }
    else if (h < 0 && v < 0) {
      offset = Math.max(-h, -v)
      angle = 225
    }

    return {
      type: 'outer',
      color: c.color.replace('#', ''),
      opacity: c.alpha,
      blur: shadow.blur / ratioPx2Pt.value,
      offset,
      angle,
    }
  }

  const dashTypeMap = {
    'solid': 'solid',
    'dashed': 'dash',
    'dotted': 'sysDot',
  }

  // 获取边框配置
  const getOutlineOption = (outline: PPTElementOutline): pptxgen.ShapeLineProps => {
    const c = formatColor(outline?.color || '#000000')
    
    return {
      color: c.color, 
      transparency: (1 - c.alpha) * 100,
      width: (outline.width || 1) / ratioPx2Pt.value, 
      dashType: outline.style ? dashTypeMap[outline.style] as 'solid' | 'dash' | 'sysDot' : 'solid',
    }
  }

  // 获取超链接配置
  const getLinkOption = (link: PPTElementLink): pptxgen.HyperlinkProps | null => {
    const { type, target } = link
    if (type === 'web') return { url: target }
    if (type === 'slide') {
      const index = slides.value.findIndex(slide => slide.id === target)
      if (index !== -1) return { slide: index + 1 }
    }

    return null
  }

  // 判断是否为Base64图片地址
  const isBase64Image = (url: string) => {
    const regex = /^data:image\/[^;]+;base64,/
    return url.match(regex) !== null
  }

  // 判断是否为SVG图片地址
  const isSVGImage = (url: string) => {
    const isSVGBase64 = /^data:image\/svg\+xml;base64,/.test(url)
    const isSVGUrl = /\.svg$/.test(url)
    return isSVGBase64 || isSVGUrl
  }

    // 添加一个辅助函数来处理外部图片URL
const getProxiedImageUrl = (url: string): string => {
  if (!url) return url
  
  try {
    const urlObj = new URL(url)
    
    // 检查是否是外部图片URL（不是base64或data URL）
    if (urlObj.origin !== window.location.origin && 
        !url.startsWith('data:image/') && 
        !url.startsWith('blob:')) {
      
      // 开发环境下使用代理
      if (import.meta.env.MODE === 'development') {
        // 只代理特定的域名
        console.log('getProxiedImageUrl', urlObj)

        if (urlObj.hostname === 'ptos.1dppt.com') {
          return `/template${urlObj.pathname}`
        }
      }
    }
  } catch (e) {
    // 如果URL解析失败，直接返回原URL
    console.warn('Invalid URL:', url, e)
  }
  
  return url
}

  /** 标准 PPTX 导出：圆角/异形裁剪/边框等 pptxgen 无法直写时，栅格化倍率 */
  const IMAGE_PPTX_RASTER_SCALE = 2

  const needsImageRasterizeForPptx = (el: PPTImageLikeElement): boolean => {
    if (el.colorMask) return true
    if (Number(el.outline?.width) > 0) return true
    if (el.radius && el.radius > 0) return true
    const shape = el.clip?.shape
    if (shape && shape !== 'rect') return true
    return false
  }

  const getImageOutlinePad = (outline?: PPTElementOutline): number => {
    const outlineWidth = Number(outline?.width) || 0
    // 描边居中在路径上，一半在元素框外；与画布 SVG overflow:visible 对齐
    return outlineWidth > 0 ? Math.ceil(outlineWidth / 2) + 1 : 0
  }

  const applyOutlinePadToPptxImageProps = (
    options: pptxgen.ImageProps,
    el: { left: number | string; top: number | string; width: number | string; height: number | string },
    pad: number,
  ) => {
    if (pad <= 0) return
    options.x = (Number(el.left) - pad) / ratioPx2Inch.value
    options.y = (Number(el.top) - pad) / ratioPx2Inch.value
    options.w = (Number(el.width) + 2 * pad) / ratioPx2Inch.value
    options.h = (Number(el.height) + 2 * pad) / ratioPx2Inch.value
  }

  const getImageOutlineLineDash = (outline: PPTElementOutline): number[] => {
    const size = Number(outline.width) || 0
    if (!size) return []
    const style = outline.style || 'solid'
    if (style === 'dashed') return size <= 6 ? [size * 4.5, size * 2] : [size * 4, size * 1.5]
    if (style === 'dotted') return size <= 6 ? [size * 1.8, size * 1.6] : [size * 1.5, size * 1.2]
    return []
  }

  const buildImageFilterCssForCanvas = (filters?: ImageElementFilters): string => {
    if (!filters) return 'none'
    const parts = (Object.keys(filters) as (keyof ImageElementFilters)[])
      .filter((k) => filters[k] != null && String(filters[k]).length > 0)
      .map((k) => `${k}(${filters[k]})`)
    return parts.length ? parts.join(' ') : 'none'
  }

  /**
   * 圆角矩形路径。不用 quadraticCurveTo：当 r≥min(w,h)/2 时直线段长度为 0，
   * 圆会变成上下左右四条「平顶」，与 CSS inset(round) 不一致。
   */
  const canvasRoundRectPath = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    radius: number,
  ) => {
    const r = Math.min(Math.max(0, radius), w / 2, h / 2)
    if (r <= 0) {
      ctx.rect(x, y, w, h)
      return
    }
    if (r >= w / 2 - 0.01 && r >= h / 2 - 0.01) {
      ctx.ellipse(x + w / 2, y + h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
      return
    }
    ctx.moveTo(x + r, y)
    ctx.arcTo(x + w, y, x + w, y + h, r)
    ctx.arcTo(x + w, y + h, x, y + h, r)
    ctx.arcTo(x, y + h, x, y, r)
    ctx.arcTo(x, y, x + w, y, r)
    ctx.closePath()
  }

  const traceImageElementClipPath = (
    ctx: CanvasRenderingContext2D,
    el: PPTImageLikeElement,
    w: number,
    h: number,
  ) => {
    const shape = el.clip?.shape || 'rect'
    const clipDef = CLIPPATHS[shape] ?? CLIPPATHS.rect

    if (clipDef.createPath) {
      return { mode: 'path2d' as const, path: new Path2D(clipDef.createPath(w, h)) }
    }
    if (clipDef.type === ClipPathTypes.ELLIPSE) {
      ctx.beginPath()
      ctx.ellipse(w / 2, h / 2, w / 2, h / 2, 0, 0, Math.PI * 2)
      return { mode: 'current' as const }
    }
    let radius = el.radius ?? 0
    if (!radius && shape === 'roundRect') {
      radius = parseFloat(String(clipDef.radius ?? '0')) || 10
    }
    ctx.beginPath()
    if (radius > 0) canvasRoundRectPath(ctx, 0, 0, w, h, radius)
    else ctx.rect(0, 0, w, h)
    return { mode: 'current' as const }
  }

  const clipImageElementRegion = (
    ctx: CanvasRenderingContext2D,
    el: PPTImageLikeElement,
    w: number,
    h: number,
  ) => {
    const traced = traceImageElementClipPath(ctx, el, w, h)
    if (traced.mode === 'path2d' && traced.path) ctx.clip(traced.path)
    else ctx.clip()
  }

  const strokeImageElementOutline = (
    ctx: CanvasRenderingContext2D,
    el: PPTImageLikeElement,
    w: number,
    h: number,
  ) => {
    const outline = el.outline
    const outlineWidth = Number(outline?.width) || 0
    if (!outlineWidth || !outline) return
    const c = formatColor(outline.color || '#000000')
    const traced = traceImageElementClipPath(ctx, el, w, h)
    ctx.save()
    ctx.strokeStyle = c.color
    ctx.globalAlpha = c.alpha
    ctx.lineWidth = outlineWidth
    ctx.setLineDash(getImageOutlineLineDash(outline))
    if (traced.mode === 'path2d' && traced.path) ctx.stroke(traced.path)
    else ctx.stroke()
    ctx.restore()
  }

  const applyElementFlipTransform = (
    ctx: CanvasRenderingContext2D,
    w: number,
    h: number,
    flipH?: boolean,
    flipV?: boolean,
  ) => {
    if (flipH && flipV) {
      ctx.translate(w / 2, h / 2)
      ctx.scale(-1, -1)
      ctx.translate(-w / 2, -h / 2)
    }
    else if (flipH) {
      ctx.translate(w / 2, 0)
      ctx.scale(-1, 1)
      ctx.translate(-w / 2, 0)
    }
    else if (flipV) {
      ctx.translate(0, h / 2)
      ctx.scale(1, -1)
      ctx.translate(0, -h / 2)
    }
  }

  const drawImageCoverInRect = (
    ctx: CanvasRenderingContext2D,
    img: HTMLImageElement,
    w: number,
    h: number,
  ) => {
    const iw = img.naturalWidth || img.width
    const ih = img.naturalHeight || img.height
    if (!iw || !ih) return
    const coverScale = Math.max(w / iw, h / ih)
    const dw = iw * coverScale
    const dh = ih * coverScale
    ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh)
  }

  const strokeShapePathOutline = (
    ctx: CanvasRenderingContext2D,
    el: PPTShapeElement,
    sx: number,
    sy: number,
  ) => {
    const outline = el.outline
    const outlineWidth = Number(outline?.width) || 0
    if (!outlineWidth || !outline) return
    const c = formatColor(outline.color || '#000000')
    const path = new Path2D(el.path)
    const lineScale = Math.max(sx, sy)
    ctx.save()
    ctx.scale(sx, sy)
    ctx.strokeStyle = c.color
    ctx.globalAlpha *= c.alpha
    ctx.lineWidth = outlineWidth / lineScale
    ctx.setLineDash(getImageOutlineLineDash(outline).map((d) => d / lineScale))
    ctx.lineCap = 'butt'
    ctx.miterLimit = 8
    ctx.stroke(path)
    ctx.restore()
  }

  const getImageDrawRectInElement = (el: PPTImageLikeElement, w: number, h: number) => {
    if (!el.clip) {
      return { x: 0, y: 0, width: w, height: h }
    }
    const [start, end] = el.clip.range
    const widthScale = (end[0] - start[0]) / 100
    const heightScale = (end[1] - start[1]) / 100
    if (widthScale <= 0 || heightScale <= 0) {
      return { x: 0, y: 0, width: w, height: h }
    }
    return {
      x: (-start[0] / widthScale / 100) * w,
      y: (-start[1] / heightScale / 100) * h,
      width: w / widthScale,
      height: h / heightScale,
    }
  }

  /**
   * 将图片元素（裁剪/圆角/边框/着色/翻转/滤镜）栅格化为 PNG，供 pptxgen addImage 使用。
   */
  const rasterizeImageLikeElementForPptx = (
    el: PPTImageLikeElement,
    src: string,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      img.onload = () => {
        try {
          const w = Math.max(1, Math.round(Number(el.width)))
          const h = Math.max(1, Math.round(Number(el.height)))
          const pad = getImageOutlinePad(el.outline)
          const cw = w + 2 * pad
          const ch = h + 2 * pad
          const scale = IMAGE_PPTX_RASTER_SCALE
          const content = document.createElement('canvas')
          content.width = cw * scale
          content.height = ch * scale
          const cctx = content.getContext('2d')
          if (!cctx) {
            reject(new Error('Canvas 2D 不可用'))
            return
          }
          cctx.scale(scale, scale)
          cctx.translate(pad, pad)

          const draw = getImageDrawRectInElement(el, w, h)
          cctx.save()
          clipImageElementRegion(cctx, el, w, h)
          cctx.filter = buildImageFilterCssForCanvas(el.filters)
          applyElementFlipTransform(cctx, w, h, el.flipH, el.flipV)
          cctx.drawImage(img, draw.x, draw.y, draw.width, draw.height)
          cctx.restore()

          const out = document.createElement('canvas')
          out.width = cw * scale
          out.height = ch * scale
          const octx = out.getContext('2d')
          if (!octx) {
            reject(new Error('Canvas 2D 不可用'))
            return
          }
          octx.scale(scale, scale)
          octx.translate(pad, pad)

          const blitContent = () => {
            const px = pad * scale
            const pw = w * scale
            const ph = h * scale
            octx.drawImage(content, px, px, pw, ph, 0, 0, w, h)
          }

          if (el.colorMask) {
            const tc = tinycolor(el.colorMask)
            octx.fillStyle = tc.getAlpha() < 1 ? tc.toRgbString() : tc.toHexString()
            octx.fillRect(0, 0, w, h)
            octx.globalCompositeOperation = 'destination-in'
            blitContent()
            octx.globalCompositeOperation = 'source-over'
          }
          else {
            blitContent()
          }

          strokeImageElementOutline(octx, el, w, h)
          resolve(out.toDataURL('image/png'))
        }
        catch (e) {
          reject(e)
        }
      }
      img.onerror = () => reject(new Error('图片加载失败'))
      img.src = src
    })
  }

  /**
   * 形状 pattern 图片填充：按 SVG path 裁剪 + cover 铺图 + 描边，栅格化为 PNG。
   */
  const rasterizeShapePatternForPptx = (
    el: PPTShapeElement,
    patternSrc: string,
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      img.onload = () => {
        try {
          const w = Math.max(1, Math.round(Number(el.width)))
          const h = Math.max(1, Math.round(Number(el.height)))
          const vbW = el.viewBox[0]
          const vbH = el.viewBox[1]
          if (!vbW || !vbH) {
            reject(new Error('形状 viewBox 无效'))
            return
          }
          const sx = w / vbW
          const sy = h / vbH
          const pad = getImageOutlinePad(el.outline)
          const cw = w + 2 * pad
          const ch = h + 2 * pad
          const scale = IMAGE_PPTX_RASTER_SCALE
          const canvas = document.createElement('canvas')
          canvas.width = cw * scale
          canvas.height = ch * scale
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Canvas 2D 不可用'))
            return
          }
          ctx.scale(scale, scale)
          ctx.translate(pad, pad)
          if (el.opacity !== undefined) ctx.globalAlpha = el.opacity

          const shapePath = new Path2D(el.path)
          ctx.save()
          ctx.scale(sx, sy)
          ctx.clip(shapePath)
          ctx.scale(1 / sx, 1 / sy)
          applyElementFlipTransform(ctx, w, h, el.flipH, el.flipV)
          drawImageCoverInRect(ctx, img, w, h)
          ctx.restore()

          strokeShapePathOutline(ctx, el, sx, sy)
          resolve(canvas.toDataURL('image/png'))
        }
        catch (e) {
          reject(e)
        }
      }
      img.onerror = () => reject(new Error('图案图片加载失败'))
      img.src = patternSrc
    })
  }

  /**
   * 特殊形状导出：直接按 path 栅格化 fill + outline。
   * 不能只把 DOM SVG 交给 PowerPoint，否则透明填充 + 描边的 SVG 在部分场景会导出为不可见图片。
   */
  const rasterizeSpecialShapeForPptx = (el: PPTShapeElement): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        const w = Math.max(1, Math.round(Number(el.width)))
        const h = Math.max(1, Math.round(Number(el.height)))
        const vbW = el.viewBox[0]
        const vbH = el.viewBox[1]
        if (!vbW || !vbH) {
          reject(new Error('形状 viewBox 无效'))
          return
        }

        const sx = w / vbW
        const sy = h / vbH
        const pad = getImageOutlinePad(el.outline)
        const cw = w + 2 * pad
        const ch = h + 2 * pad
        const scale = IMAGE_PPTX_RASTER_SCALE
        const canvas = document.createElement('canvas')
        canvas.width = cw * scale
        canvas.height = ch * scale
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('Canvas 2D 不可用'))
          return
        }

        ctx.scale(scale, scale)
        ctx.translate(pad, pad)
        if (el.opacity !== undefined) ctx.globalAlpha = el.opacity

        const shapePath = new Path2D(el.path)
        let fillColor = formatColor(el.fill)
        if (el.gradient) {
          const colors = el.gradient.colors
          const color1 = colors[0].color
          const color2 = colors[colors.length - 1].color
          fillColor = formatColor(tinycolor.mix(color1, color2).toHexString())
        }

        ctx.save()
        ctx.scale(sx, sy)
        ctx.fillStyle = fillColor.color
        ctx.globalAlpha *= fillColor.alpha
        ctx.fill(shapePath)
        ctx.restore()

        strokeShapePathOutline(ctx, el, sx, sy)
        resolve(canvas.toDataURL('image/png'))
      }
      catch (e) {
        reject(e)
      }
    })
  }

  /**
   * 将「图标着色」栅格化为 PNG dataURL，与画布上 mask + backgroundColor 效果一致（destination-in）。
   * 用于标准导出 PPTX：pptxgen 无法表达 CSS mask。
   */
  const tintImageSrcWithColorMask = (src: string, colorMask: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      img.onload = () => {
        try {
          const w = img.naturalWidth || img.width
          const h = img.naturalHeight || img.height
          const canvas = document.createElement('canvas')
          canvas.width = Math.max(1, w)
          canvas.height = Math.max(1, h)
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Canvas 2D 不可用'))
            return
          }
          const tc = tinycolor(colorMask)
          ctx.fillStyle = tc.getAlpha() < 1 ? tc.toRgbString() : tc.toHexString()
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.globalCompositeOperation = 'destination-in'
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
          resolve(canvas.toDataURL('image/png'))
        }
        catch (e) {
          reject(e)
        }
      }
      img.onerror = () => reject(new Error('图片加载失败'))
      img.src = src
    })
  }

  /** 后端导出 PPTX（测试） */
  const exportPPTXViaBackend = async (
    pptxId: string,
    pptxType: 'url' | 'stream' = 'url',
    fileName?: string,
  ) => {
    throwIfExportCancelled()
    const requestBody = {
      params: {
        id: pptxId,
        output: pptxType,
        options: {
          ignoreMedia: true,
          masterOverwrite: true,
        },
      },
    }
    const finalFileName = sanitizeExportBaseName(fileName || title.value)
    const fn = `${finalFileName}.pptx`

    if (pptxType === 'stream') {
      const blob = await exportPptxStream(requestBody as { params: { id: string; output: 'stream'; options?: Record<string, boolean> } }) as Blob
      throwIfExportCancelled()
      if (isMobile()) {
        await uploadFileToNative(blob, fn, 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'TEMPORARY_FILE', undefined, 'ppt')
      } else {
        saveAs(blob, fn)
      }
      return
    }

    const res = await exportPptx(requestBody) as {
      code: string
      data?: string | { url?: string; fileUrl?: string; downloadUrl?: string }
      msg?: string
    }
    if (res.code !== 'SUCCESS') {
      throw new Error(res.msg || '导出PPT失败')
    }
    const url = typeof res.data === 'string'
      ? res.data
      : (res.data?.url || res.data?.fileUrl || res.data?.downloadUrl)
    if (!url) {
      console.log('[exportPPTXViaBackend] 响应:', res)
      throw new Error('未获取到PPT下载链接')
    }
    throwIfExportCancelled()
    if (isMobile()) {
      const win = window as Window & { JsAndroid?: { downFile: (name: string, url: string, type: string) => void }; prompt?: (s: string) => string | null }
      setupDownProgress()
      if (isMiniAppWebview()) {
        requestMiniAppDownload({
          fileName: fn,
          fileUrl: url,
          fileType: 'PPTX',
        })
      } else if (win.JsAndroid?.downFile) {
        win.JsAndroid.downFile(fn, url, 'PPTX')
      } else if (win.prompt) {
        const iosFileName = appendTimestampForIosFileName(fn)
        win.prompt(`ydppt://downFile?fileName=${encodeURIComponent(iosFileName)}&fileUrl=${encodeURIComponent(url)}&fileType=${encodeURIComponent('PPTX')}&closeHid=true`)
      }
    } else {
      try {
        const response = await fetch(url)
        if (!response.ok) {
          throw new Error(`下载PPT失败(${response.status})`)
        }
        const blob = await response.blob()
        throwIfExportCancelled()
        saveAs(blob, fn)
      } catch (e) {
        if (e instanceof ExportCancelledError || isExportCancelled()) throw e
        throw createExportUrlDownloadError(url, e)
      }
    }
  }

  // 导出PPTX文件
  const exportPPTX = async (
    _slides: Slide[],
    masterOverwrite: boolean,
    ignoreMedia: boolean,
    fileName?: string,
    options?: ExportProgressOptions,
    pptxId?: string,
    pptxType?: string,
  ) => {
    const disableLoading = !!options?.disableLoading
    resetExportCancelState()
    if (!disableLoading) {
      openExportLoading('', { variant: 'ppt' })
    }
    exporting.value = true
    let pptProgressTimer: ReturnType<typeof setInterval> | null = null
    if (disableLoading && options?.onProgress) {
      const start = Date.now()
      options.onProgress(0)
      pptProgressTimer = setInterval(() => {
        const elapsed = Date.now() - start
        const p = Math.min(PPT_FAKE_PROGRESS_CAP, (elapsed / PPT_FAKE_PROGRESS_DURATION_MS) * PPT_FAKE_PROGRESS_CAP)
        options.onProgress?.(p)
      }, 120)
    }
    let pptExportHadError = false
    try {
      console.log('[exportPPTX] slides:', pptxId)
    if (pptxId) {
      await exportPPTXViaBackend(pptxId, (pptxType === 'stream' ? 'stream' : 'url'), fileName)
      return
    }
    const pptx = new pptxgen()

    if (viewportRatio.value === 0.625) pptx.layout = 'LAYOUT_16x10'
    else if (viewportRatio.value === 0.75) pptx.layout = 'LAYOUT_4x3'
    else if (viewportRatio.value === 0.70710678) {
      pptx.defineLayout({ name: 'A3', width: 10, height: 7.0710678 })
      pptx.layout = 'A3'
    }
    else if (viewportRatio.value === 1.41421356) {
      pptx.defineLayout({ name: 'A3_V', width: 10, height: 14.1421356 })
      pptx.layout = 'A3_V'
    }
    else pptx.layout = 'LAYOUT_16x9'
    if (masterOverwrite) {
      const { color: bgColor, alpha: bgAlpha } = formatColor(theme.value.backgroundColor)
      pptx.defineSlideMaster({
        title: 'PPTIST_MASTER',
        background: { color: bgColor, transparency: (1 - bgAlpha) * 100 },
      })
    }

   // 与画布逻辑宽度一致（如 1280×720 → 10"×5.625"），元素坐标按同一比例换算
    const slideW = viewportSize.value / ratioPx2Inch.value
    const slideH = (viewportSize.value * viewportRatio.value) / ratioPx2Inch.value

    for (const slide of _slides) {
      throwIfExportCancelled()
      const pptxSlide = pptx.addSlide()

      if (slide.background) {
        const background = slide.background
        if (background.type === 'image' && background.image) {
              let imgSrc = background.image.src

          // 页面预览使用 CSS background-size: cover|contain（见 useSlideBackgroundStyle）。
          // 若用 pptxSlide.background 填路径，PowerPoint 往往按「拉伸铺满」处理，和 cover 不一致，
          // 会导致底图与按像素定位的图层错位。改用置于最底层的整页图片 + sizing 对齐浏览器效果。
          const sizeMode = background.image.size || 'cover'
          const bgImageOpts: pptxgen.ImageProps = {
            x: 0,
            y: 0,
            w: slideW,
            h: slideH,
          }
          if (sizeMode === 'cover' || sizeMode === 'contain') {
            bgImageOpts.sizing = { type: sizeMode, w: slideW, h: slideH }
          }

          // 在开发环境中对非Base64/SVG图片进行代理处理
          if (import.meta.env.MODE === 'development' &&  !isBase64Image(imgSrc) && !isSVGImage(imgSrc)) {
            imgSrc = getProxiedImageUrl(imgSrc)
          }
        

          if (isSVGImage(imgSrc)) {
            pptxSlide.addImage({
              ...bgImageOpts, 
              data: imgSrc,
            })
          }
          else if (isBase64Image(imgSrc)) {
            pptxSlide.background = {  ...bgImageOpts, data: imgSrc }
          }
          else {
            pptxSlide.background = {  ...bgImageOpts, path: imgSrc }
          }
        }
        else if (background.type === 'solid' && background.color) {
          const c = formatColor(background.color)
          pptxSlide.background = { color: c.color, transparency: (1 - c.alpha) * 100 }
        }
        else if (background.type === 'gradient' && background.gradient) {
          const colors = background.gradient.colors
          const color1 = colors[0].color
          const color2 = colors[colors.length - 1].color
          const color = tinycolor.mix(color1, color2).toHexString()
          const c = formatColor(color)
          pptxSlide.background = { color: c.color, transparency: (1 - c.alpha) * 100 }
        }
      }
      if (slide.remark) {
        const doc = new DOMParser().parseFromString(slide.remark, 'text/html')
        const pList = doc.body.querySelectorAll('p')
        const text = []
        for (const p of pList) {
          const textContent = p.textContent
          text.push(textContent || '')
        }
        pptxSlide.addNotes(text.join('\n'))
      }

      if (!slide.elements) continue

      for (const el of slide.elements) {
        throwIfExportCancelled()
        if (el.type === 'text') {
          const textProps = formatHTML(el.content)

          const options: pptxgen.TextPropsOptions = {
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: el.width / ratioPx2Inch.value,
            h: el.height / ratioPx2Inch.value,
            fontSize: defaultFontSize / ratioPx2Pt.value,
            fontFace: '微软雅黑',
            color: '#000000',
            valign: el.align === 'top' ? 'top' : el.align === 'bottom' ? 'bottom' : 'middle',
            margin: 10 / ratioPx2Pt.value,
            paraSpaceBefore: 5 / ratioPx2Pt.value,
            // 与编辑器默认 line-height: 1 对齐；WPS 对自定义行距常显示为空，显式写 1 可避免偏差
            lineSpacingMultiple: 1,
            autoFit: true,
          }
          if (el.rotate) options.rotate = el.rotate
          if (el.wordSpace) options.charSpacing = el.wordSpace / ratioPx2Pt.value
          if (el.lineHeight) options.lineSpacingMultiple = el.lineHeight
          if (el.fill) {
            const c = formatColor(el.fill)
            const opacity = el.opacity === undefined ? 1 : el.opacity
            options.fill = { color: c.color, transparency: (1 - c.alpha * opacity) * 100 }
          }
          if (el.defaultColor) options.color = formatColor(el.defaultColor).color
          if (el.defaultFontName) options.fontFace = el.defaultFontName
          if (el.shadow) options.shadow = getShadowOption(el.shadow)
          if (el.outline?.width) options.line = getOutlineOption(el.outline)
          if (el.opacity !== undefined) options.transparency = (1 - el.opacity) * 100
          if (el.paragraphSpace !== undefined) options.paraSpaceBefore = el.paragraphSpace / ratioPx2Pt.value
          if (el.vertical) options.vert = 'eaVert'
          options.objectName = el.id

          pptxSlide.addText(textProps, options)
        }

        else if (isImageLikeElement(el)) {

          const imgEl = el as PPTImageLikeElement
          let imgSrc = el.src
           // 在开发环境中对非Base64/SVG图片进行代理处理
          if (import.meta.env.MODE === 'development' &&  !isBase64Image(imgSrc) && !isSVGImage(imgSrc)) {
            imgSrc = getProxiedImageUrl(imgSrc)
          }

          let imageRasterized = false
          const imageOutlinePad = getImageOutlinePad(imgEl.outline)
          if (needsImageRasterizeForPptx(imgEl)) {
            try {
              imgSrc = await rasterizeImageLikeElementForPptx(imgEl, imgSrc)
              imageRasterized = true
            }
            catch (e) {
              console.warn('导出 PPTX：图片样式栅格化失败，回退为矢量导出', e)
            }
          }
          else if (imgEl.colorMask) {
            try {
              imgSrc = await tintImageSrcWithColorMask(imgEl.src, imgEl.colorMask)
              imageRasterized = true
            }
            catch (e) {
              console.warn('导出 PPTX：图标着色栅格化失败，使用原图', e)
              imgSrc = imgEl.src
            }
          }

          const options: pptxgen.ImageProps = {
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: el.width / ratioPx2Inch.value,
            h: el.height / ratioPx2Inch.value,
          }
          if (imageRasterized && imageOutlinePad > 0) {
            applyOutlinePadToPptxImageProps(options, el, imageOutlinePad)
          }
          if (isBase64Image(imgSrc)) options.data = imgSrc
          else options.path = imgSrc

          if (!imageRasterized) {
            if (el.flipH) options.flipH = el.flipH
            if (el.flipV) options.flipV = el.flipV
          }
          if (el.rotate) options.rotate = el.rotate
          if (el.link) {
            const linkOption = getLinkOption(el.link)
            if (linkOption) options.hyperlink = linkOption
          }
          if (!imageRasterized && el.filters?.opacity) {
            options.transparency = 100 - parseInt(el.filters?.opacity)
          }
          // clip.range 为 0–100 的百分比，与画布 useClipImage.ts 一致（非像素）。
          // 圆角/边框/异形裁剪已栅格化进 PNG，不再走 pptxgen crop/rounding。
          if (!imageRasterized && el.clip) {
            if (el.clip.shape === 'ellipse') options.rounding = true

            const [start, end] = el.clip.range
            const [startX, startY] = start
            const [endX, endY] = end
            const widthScale = (endX - startX) / 100
            const heightScale = (endY - startY) / 100

             if (widthScale > 0 && heightScale > 0) {
              const originWIn = el.width / widthScale / ratioPx2Inch.value
              const originHIn = el.height / heightScale / ratioPx2Inch.value
              options.w = originWIn
              options.h = originHIn
              options.sizing = {
                type: 'crop',
                x: (startX / 100) * originWIn,
                y: (startY / 100) * originHIn,
                w: widthScale * originWIn,
                h: heightScale * originHIn,
              }
            }
          }
          options.objectName = el.id

          pptxSlide.addImage(options)
        }

        else if (el.type === 'shape') {
          let shapePatternRasterized = false

          if (el.special) {
            const rasterData = await rasterizeSpecialShapeForPptx(el)
            const shapeOutlinePad = getImageOutlinePad(el.outline)
            const options: pptxgen.ImageProps = {
              data: rasterData,
              x: el.left / ratioPx2Inch.value,
              y: el.top / ratioPx2Inch.value,
              w: el.width / ratioPx2Inch.value,
              h: el.height / ratioPx2Inch.value,
            }
            if (shapeOutlinePad > 0) {
              applyOutlinePadToPptxImageProps(options, el, shapeOutlinePad)
            }
            if (el.rotate) options.rotate = el.rotate
            if (el.flipH) options.flipH = el.flipH
            if (el.flipV) options.flipV = el.flipV
            if (el.link) {
              const linkOption = getLinkOption(el.link)
              if (linkOption) options.hyperlink = linkOption
            }
            options.objectName = el.id

            pptxSlide.addImage(options)
          }
          else {
            if (el.pattern) {
              let patternSrc = el.pattern
              if (import.meta.env.MODE === 'development' && !isBase64Image(patternSrc) && !isSVGImage(patternSrc)) {
                patternSrc = getProxiedImageUrl(patternSrc)
              }
              try {
                const rasterData = await rasterizeShapePatternForPptx(el, patternSrc)
                const shapeOutlinePad = getImageOutlinePad(el.outline)
                const patternImgOpts: pptxgen.ImageProps = {
                  data: rasterData,
                  x: el.left / ratioPx2Inch.value,
                  y: el.top / ratioPx2Inch.value,
                  w: el.width / ratioPx2Inch.value,
                  h: el.height / ratioPx2Inch.value,
                }
                if (shapeOutlinePad > 0) {
                  applyOutlinePadToPptxImageProps(patternImgOpts, el, shapeOutlinePad)
                }
                if (el.rotate) patternImgOpts.rotate = el.rotate
                if (el.shadow) patternImgOpts.shadow = getShadowOption(el.shadow)
                if (el.link) {
                  const linkOption = getLinkOption(el.link)
                  if (linkOption) patternImgOpts.hyperlink = linkOption
                }
                patternImgOpts.objectName = el.id
                pptxSlide.addImage(patternImgOpts)
                shapePatternRasterized = true
              }
              catch (e) {
                console.warn('导出 PPTX：形状图案填充栅格化失败，回退为矢量导出', e)
              }
            }

            if (!shapePatternRasterized) {
              const scale = {
                x: el.width / el.viewBox[0],
                y: el.height / el.viewBox[1],
              }
              const points = formatPoints(toPoints(el.path), scale)
    
              let fillColor = formatColor(el.fill)
              if (el.gradient) {
                const colors = el.gradient.colors
                const color1 = colors[0].color
                const color2 = colors[colors.length - 1].color
                const color = tinycolor.mix(color1, color2).toHexString()
                fillColor = formatColor(color)
              }
              if (el.pattern) fillColor = formatColor('#00000000')
              const opacity = el.opacity === undefined ? 1 : el.opacity
    
              const options: pptxgen.ShapeProps = {
                x: el.left / ratioPx2Inch.value,
                y: el.top / ratioPx2Inch.value,
                w: el.width / ratioPx2Inch.value,
                h: el.height / ratioPx2Inch.value,
                fill: { color: fillColor.color, transparency: (1 - fillColor.alpha * opacity) * 100 },
                points,
              }
              if (el.flipH) options.flipH = el.flipH
              if (el.flipV) options.flipV = el.flipV
              if (el.shadow) options.shadow = getShadowOption(el.shadow)
              if (Number(el.outline?.width) > 0) options.line = getOutlineOption(el.outline!)
              if (el.rotate) options.rotate = el.rotate
              if (el.link) {
                const linkOption = getLinkOption(el.link)
                if (linkOption) options.hyperlink = linkOption
              }
              options.objectName = el.id

              pptxSlide.addShape('custGeom' as pptxgen.ShapeType, options)
            }
          }
          if (el.text) {
            const textProps = formatHTML(el.text.content)

            const options: pptxgen.TextPropsOptions = {
              x: el.left / ratioPx2Inch.value,
              y: el.top / ratioPx2Inch.value,
              w: el.width / ratioPx2Inch.value,
              h: el.height / ratioPx2Inch.value,
              fontSize: defaultFontSize / ratioPx2Pt.value,
              fontFace: '微软雅黑',
              color: '#000000',
              paraSpaceBefore: 5 / ratioPx2Pt.value,
              valign: el.text.align,
              lineSpacingMultiple: el.text.lineHeight || 1,
            }
            if (el.rotate) options.rotate = el.rotate
            if (el.text.defaultColor) options.color = formatColor(el.text.defaultColor).color
            if (el.text.defaultFontName) options.fontFace = el.text.defaultFontName
            options.objectName = `${el.id}__text`

            pptxSlide.addText(textProps, options)
          }
          if (el.pattern && !shapePatternRasterized) {
            let patternSrc = el.pattern
            if (import.meta.env.MODE === 'development' && !isBase64Image(patternSrc) && !isSVGImage(patternSrc)) {
              patternSrc = getProxiedImageUrl(patternSrc)
            }
            const options: pptxgen.ImageProps = {
              x: el.left / ratioPx2Inch.value,
              y: el.top / ratioPx2Inch.value,
              w: el.width / ratioPx2Inch.value,
              h: el.height / ratioPx2Inch.value,
            }
            if (isBase64Image(patternSrc)) options.data = patternSrc
            else options.path = patternSrc
  
            if (el.flipH) options.flipH = el.flipH
            if (el.flipV) options.flipV = el.flipV
            if (el.rotate) options.rotate = el.rotate
            if (el.link) {
              const linkOption = getLinkOption(el.link)
              if (linkOption) options.hyperlink = linkOption
            }

            pptxSlide.addImage(options)
          }
        }

        else if (el.type === 'line') {
          const path = getLineElementPath(el)
          const points = formatPoints(toPoints(path))
          const { minX, maxX, minY, maxY } = getElementRange(el)
          const c = formatColor(el.color)

          const options: pptxgen.ShapeProps = {
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: (maxX - minX) / ratioPx2Inch.value,
            h: (maxY - minY) / ratioPx2Inch.value,
            line: {
              color: c.color, 
              transparency: (1 - c.alpha) * 100,
              width: el.width / ratioPx2Pt.value, 
              dashType: dashTypeMap[el.style] as 'solid' | 'dash' | 'sysDot',
              beginArrowType: el.points[0] ? 'arrow' : 'none',
              endArrowType: el.points[1] ? 'arrow' : 'none',
            },
            points,
          }
          if (el.shadow) options.shadow = getShadowOption(el.shadow)
          options.objectName = el.id

          pptxSlide.addShape('custGeom' as pptxgen.ShapeType, options)
        }

        else if (el.type === 'chart') {
          const chartPayload = el.data
          const hasValidChartData = chartPayload
            && Array.isArray(chartPayload.series)
            && chartPayload.series.length > 0
            && Array.isArray(chartPayload.labels)
            && chartPayload.labels.length > 0
            && chartPayload.series.some(item => Array.isArray(item) && item.length > 0)
          if (!hasValidChartData) continue

          const chartData = []
          for (let i = 0; i < chartPayload.series.length; i++) {
            const item = chartPayload.series[i]
            if (!Array.isArray(item) || item.length === 0) continue
            chartData.push({
              name: chartPayload.legends?.[i] || `系列${i + 1}`,
              labels: chartPayload.labels,
              values: item,
            })
          }
          if (chartData.length === 0) continue

          let chartColors: string[] = []
          if (el.themeColors.length === 10) chartColors = el.themeColors.map(color => formatColor(color).color)
          else if (el.themeColors.length === 1) chartColors = tinycolor(el.themeColors[0]).analogous(10).map(color => formatColor(color.toHexString()).color)
          else {
            const len = el.themeColors.length
            const supplement = tinycolor(el.themeColors[len - 1]).analogous(10 + 1 - len).map(color => color.toHexString())
            chartColors = [...el.themeColors.slice(0, len - 1), ...supplement].map(color => formatColor(color).color)
          }
          
          const options: pptxgen.IChartOpts = {
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: el.width / ratioPx2Inch.value,
            h: el.height / ratioPx2Inch.value,
            chartColors: (el.chartType === 'pie' || el.chartType === 'ring') ? chartColors : chartColors.slice(0, el.data.series.length),
          }

          const textColor = formatColor(el.textColor || '#000000').color
          options.catAxisLabelColor = textColor
          options.valAxisLabelColor = textColor

          const fontSize = 14 / ratioPx2Pt.value
          options.catAxisLabelFontSize = fontSize
          options.valAxisLabelFontSize = fontSize
          
          if (el.fill || el.outline) {
            const plotArea: pptxgen.IChartPropsFillLine = {}
            if (el.fill) {
              plotArea.fill = { color: formatColor(el.fill).color }
            }
            if (el.outline) {
              plotArea.border = {
                pt: el.outline.width! / ratioPx2Pt.value,
                color: formatColor(el.outline.color!).color,
              }
            }
            options.plotArea = plotArea
          }

          if ((el.data.series.length > 1 && el.chartType !== 'scatter') || el.chartType === 'pie' || el.chartType === 'ring') {
            options.showLegend = true
            options.legendPos = 'b'
            options.legendColor = textColor
            options.legendFontSize = fontSize
          }

          let type = pptx.ChartType.bar
          if (el.chartType === 'bar') {
            type = pptx.ChartType.bar
            options.barDir = 'col'
            if (el.options?.stack) options.barGrouping = 'stacked'
          }
          else if (el.chartType === 'column') {
            type = pptx.ChartType.bar
            options.barDir = 'bar'
            if (el.options?.stack) options.barGrouping = 'stacked'
          }
          else if (el.chartType === 'line') {
            type = pptx.ChartType.line
            if (el.options?.lineSmooth) options.lineSmooth = true
          }
          else if (el.chartType === 'area') {
            type = pptx.ChartType.area
          }
          else if (el.chartType === 'radar') {
            type = pptx.ChartType.radar
          }
          else if (el.chartType === 'scatter') {
            type = pptx.ChartType.scatter
            options.lineSize = 0
          }
          else if (el.chartType === 'pie') {
            type = pptx.ChartType.pie
          }
          else if (el.chartType === 'ring') {
            type = pptx.ChartType.doughnut
            options.holeSize = 60
          }
          options.objectName = el.id
          
          pptxSlide.addChart(type, chartData, options)
        }

        else if (el.type === 'table') {
          const hiddenCells = []
          for (let i = 0; i < el.data.length; i++) {
            const rowData = el.data[i]

            for (let j = 0; j < rowData.length; j++) {
              const cell = rowData[j]
              if (cell.colspan > 1 || cell.rowspan > 1) {
                for (let row = i; row < i + cell.rowspan; row++) {
                  for (let col = row === i ? j + 1 : j; col < j + cell.colspan; col++) hiddenCells.push(`${row}_${col}`)
                }
              }
            }
          }

          const tableData = []

          const theme = el.theme
          let themeColor: FormatColor | null = null
          let subThemeColors: FormatColor[] = []
          if (theme) {
            themeColor = formatColor(theme.color)
            subThemeColors = getTableSubThemeColor(theme.color).map(item => formatColor(item))
          }

          for (let i = 0; i < el.data.length; i++) {
            const row = el.data[i]
            const _row = []

            for (let j = 0; j < row.length; j++) {
              const cell = row[j]
              const cellOptions: pptxgen.TableCellProps = {
                colspan: cell.colspan,
                rowspan: cell.rowspan,
                bold: cell.style?.bold || false,
                italic: cell.style?.em || false,
                underline: { style: cell.style?.underline ? 'sng' : 'none' },
                align: cell.style?.align || 'left',
                valign: 'middle',
                fontFace: cell.style?.fontname || '微软雅黑',
                fontSize: (cell.style?.fontsize ? Number.parseFloat(cell.style?.fontsize) : 14) / ratioPx2Pt.value,
              }
              if (theme && themeColor) {
                let c: FormatColor
                if (i % 2 === 0) c = subThemeColors[1]
                else c = subThemeColors[0]

                if (theme.rowHeader && i === 0) c = themeColor
                else if (theme.rowFooter && i === el.data.length - 1) c = themeColor
                else if (theme.colHeader && j === 0) c = themeColor
                else if (theme.colFooter && j === row.length - 1) c = themeColor

                cellOptions.fill = { color: c.color, transparency: (1 - c.alpha) * 100 }
              }
              if (cell.style?.backcolor) {
                const c = formatColor(cell.style.backcolor)
                cellOptions.fill = { color: c.color, transparency: (1 - c.alpha) * 100 }
              }
              if (cell.style?.color) cellOptions.color = formatColor(cell.style.color).color

              if (!hiddenCells.includes(`${i}_${j}`)) {
                _row.push({
                  text: cell.text,
                  options: cellOptions,
                })
              }
            }
            if (_row.length) tableData.push(_row)
          }

          const options: pptxgen.TableProps = {
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: el.width / ratioPx2Inch.value,
            h: el.height / ratioPx2Inch.value,
            colW: el.colWidths.map(item => el.width * item / ratioPx2Inch.value),
          }
          if (el.theme) options.fill = { color: '#ffffff' }
          if (el.outline.width && el.outline.color) {
            options.border = {
              type: el.outline.style === 'solid' ? 'solid' : 'dash',
              pt: el.outline.width / ratioPx2Pt.value,
              color: formatColor(el.outline.color).color,
            }
          }
          options.objectName = el.id

          pptxSlide.addTable(tableData, options)
        }
        
        else if (el.type === 'latex') {
          const svgRef = document.querySelector(`.thumbnail-list .base-element-${el.id} svg`) as HTMLElement | null
          if (!svgRef) continue
          const base64SVG = svg2Base64(svgRef)

          const options: pptxgen.ImageProps = {
            data: base64SVG,
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: el.width / ratioPx2Inch.value,
            h: el.height / ratioPx2Inch.value,
          }
          if (el.link) {
            const linkOption = getLinkOption(el.link)
            if (linkOption) options.hyperlink = linkOption
          }
          options.objectName = el.id

          pptxSlide.addImage(options)
        }

        else if (!ignoreMedia && (el.type === 'video' || el.type === 'audio')) {

         // 在开发环境中对音频视频进行代理处理
        // let mediaSrc = el.src
        // let posterSrc = el.poster || undefined
        
        // if (import.meta.env.MODE === 'development') {
        //   mediaSrc = getProxiedImageUrl(mediaSrc)
        //   if (posterSrc) {
        //     posterSrc = getProxiedImageUrl(posterSrc)
        //   }
        // }

          const options: pptxgen.MediaProps = {
            x: el.left / ratioPx2Inch.value,
            y: el.top / ratioPx2Inch.value,
            w: el.width / ratioPx2Inch.value,
            h: el.height / ratioPx2Inch.value,
            path: el.src,
            type: el.type,
          }
          if (el.type === 'video' && el.poster) options.cover = el.poster

          const extMatch = el.src.match(/\.([a-zA-Z0-9]+)(?:[\?#]|$)/)
          if (extMatch && extMatch[1]) options.extn = extMatch[1]
          else if (el.ext) options.extn = el.ext
          
          const videoExts = ['avi', 'mp4', 'm4v', 'mov', 'wmv']
          const audioExts = ['mp3', 'm4a', 'mp4', 'wav', 'wma']
          if (options.extn && [...videoExts, ...audioExts].includes(options.extn)) {
            options.objectName = el.id
            pptxSlide.addMedia(options)
          }
        }
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 200))
    if (isExportCancelled()) {
      exporting.value = false
      return
    }
    const finalFileName = sanitizeExportBaseName(fileName || title.value)
    const fn = `${finalFileName}.pptx`
    throwIfExportCancelled()
    let blob = await pptx.write({ outputType: 'blob' }) as Blob
    blob = await injectPptxAnimations(blob, _slides)
    blob = await injectAIGCMetadataToPPTX(blob)
    if (isMobile()) {
      await uploadFileToNative(blob, fn, 'application/vnd.openxmlformats-officedocument.presentationml.presentation', 'TEMPORARY_FILE', undefined, 'ppt')
    } else {
      saveAs(blob, fn)
    }
    } catch (e) {
      if (!isExportCancelled()) {
        pptExportHadError = true
        console.error(isExportUploadError(e) ? '上传PPT失败:' : '导出PPT失败:', e)
        ElMessage.error({ message: getExportErrorMessage(e), offset: 60 })
        const fallbackUrl = getExportDownloadUrl(e)
        if (fallbackUrl) {
          showPptManualDownloadDialog(fallbackUrl)
        }
      }
    } finally {
      if (pptProgressTimer) {
        clearInterval(pptProgressTimer)
      }
      exporting.value = false
      if (disableLoading) {
        options?.onProgress?.(100)
      } else if (pptExportHadError || !exportOverlayRoot) {
        closeExportLoading()
      } else {
        stopPptFakeProgress()
        setExportPptPercent(100)
        showExportOverlaySuccessThenClose()
      }
    }
  }

  const beginMobileExportBatch = () => {
    resetExportCancelState()
    exporting.value = true
  }

  const endMobileExportBatch = () => {
    exporting.value = false
  }

  /** 移动端分批 PDF：逐页 append，finalize 时统一注入 AIGC 元数据，避免中间页占满内存 */
  const createMobilePdfMerger = () => {
    let merged: PDFDocument | null = null
    return {
      async append(pageBlob: Blob) {
        if (!pageBlob?.size) return
        const bytes = await pageBlob.arrayBuffer()
        const doc = await PDFDocument.load(bytes, { ignoreEncryption: true })
        if (!merged) merged = await PDFDocument.create()
        const pages = await merged.copyPages(doc, doc.getPageIndices())
        pages.forEach((page) => merged!.addPage(page))
        await yieldMobileExportGc(60)
      },
      async finalize(): Promise<Blob> {
        if (!merged) throw new Error('没有可合并的 PDF 页面')
        const bytes = await merged.save()
        merged = null
        let blob = new Blob([new Uint8Array(bytes)], { type: 'application/pdf' })
        blob = await injectAIGCMetadataToPDF(blob)
        return blob
      },
    }
  }

  return {
    exporting,
    cancelExport,
    exportImage,
    exportPDF,
    exportImagePPTX,
    exportJSON,
    exportSpecificFile,
    exportPPTX,
    uploadFilesToIOSBatch,
    getIosExportImagePageChunkSize,
    getAndroidExportImagePageChunkSize,
    getMobileExportImagePageChunk,
    finalizeMobileDeferredImagesAsLongStrip,
    /** 分段多图已上传：iOS fileList 批量；安卓依次 prompt/downFile */
    finalizeMobileDeferredSegmentDownloads,
    /** 移动端合并后的导出文件上传（PDF 等） */
    uploadExportBlob: (
      blob: Blob,
      fileName: string,
      mimeType: string,
      kind: 'pdf' | 'image' | 'image_long_strip' | 'ppt' = 'pdf',
    ) => uploadFileToNative(blob, fileName, mimeType, 'TEMPORARY_FILE', undefined, kind),
    /** @deprecated 使用 finalizeMobileDeferredSegmentDownloads；长图拼接仅作备用 */
    finalizeIosDeferredImagesAsLongStrip: finalizeMobileDeferredImagesAsLongStrip,
    /** iOS 按页导出图片 defer 批量下载后，由外层调用以展示「下载成功」并关闭遮罩 */
    showExportDownloadSuccess: showExportOverlaySuccessThenClose,
    beginMobileExportBatch,
    endMobileExportBatch,
    createMobilePdfMerger,
    yieldMobileExportGc,
    /** iOS 原生保存进度（downProgress 回调） */
    registerNativeSaveProgress,
    /** 打开/关闭导出遮罩（工作台下载前获取详情等准备阶段可用 preparing） */
    openExportLoading,
    closeExportLoading,
  }
}
